/**
 * Static MSW Handlers
 *
 * These handlers have custom logic that cannot be auto-generated
 * from controller definitions. They handle:
 * - Authentication bypass
 * - SSE streams
 * - FX rates (dynamic date ranges)
 * - File uploads
 * - Special endpoints
 */

import { http, HttpResponse, HttpHandler} from 'msw';

// Demo user info (bypasses Auth0)
const DEMO_USER = {
  authenticatedInfo: {
    id: 'usr_01jwvb5j4he06aqw6yspcywgds',
    email: 'demo@acmetech.com',
    emailVerified: true,
    isOrgReady: true,
    name: 'Demo User',
    nickname: 'demo',
    picture:
      'https://s.gravatar.com/avatar/ab08bddf23287c4b639f71a352515b95?s=480&r=pg&d=https%3A%2F%2Fcdn.auth0.com%2Favatars%2Fde.png',
    activeOrg: {
      id: 'org_01k1y1x3vmfqfbnc337k9crvrg',
      name: 'Acme Tech',
      logoUrl: null,
    },
  },
  isAuthenticated: true,
};

// FX rates data (EUR and GBP base currencies)
const EUR_RATES: Record<string, number> = {
  AED: 3.819415,
  AUD: 1.654532,
  CAD: 1.488638,
  CHF: 0.937412,
  CNY: 7.561287,
  EUR: 1,
  GBP: 0.831794,
  HKD: 8.097157,
  INR: 90.816397,
  JPY: 158.7354,
  KRW: 1502.895448,
  MXN: 21.40298,
  NOK: 11.674742,
  NZD: 1.828289,
  SEK: 11.345633,
  SGD: 1.402623,
  USD: 1.039867,
  ZAR: 19.288489,
};

const GBP_RATES: Record<string, number> = {
  AED: 4.591782,
  AUD: 1.989114,
  CAD: 1.789673,
  CHF: 1.126976,
  CNY: 9.090341,
  EUR: 1.202221,
  GBP: 1,
  HKD: 9.734574,
  INR: 109.181408,
  JPY: 190.83508,
  KRW: 1806.81293,
  MXN: 25.731118,
  NOK: 14.035624,
  NZD: 2.198008,
  SEK: 13.639962,
  SGD: 1.686263,
  USD: 1.25015,
  ZAR: 23.189033,
};

/**
 * Generate dates between start and end
 */
function getDatesBetween(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  const current = new Date(start);

  while (current <= end) {
    dates.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 1);
  }

  return dates;
}

/**
 * Generate FX rates for a date range
 */
function generateFxRates(base: string, start: string, end: string): Record<string, Record<string, number>> {
  const rates: Record<string, Record<string, number>> = {};
  const baseRates = base === 'EUR' ? EUR_RATES : GBP_RATES;

  for (const date of getDatesBetween(start, end)) {
    // Add small random variance to make it look realistic
    rates[date] = {};
    for (const [currency, rate] of Object.entries(baseRates)) {
      const variance = 1 + (Math.random() - 0.5) * 0.001; // ±0.05% variance
      rates[date][currency] = rate * variance;
    }
  }

  return rates;
}

/**
 * Generate TypeID-like IDs
 */
function generateId(prefix: string): string {
  const chars = '0123456789abcdefghjkmnpqrstvwxyz';
  let id = '';
  for (let i = 0; i < 26; i++) {
    id += chars[Math.floor(Math.random() * chars.length)];
  }
  return `${prefix}_${id}`;
}

/**
 * Static handlers for special endpoints
 */
export const staticHandlers:HttpHandler[] = [
  // Auth bypass - return demo user
  http.get('/auth/user-info', () => {
    return HttpResponse.json(DEMO_USER);
  }),

  // SSE stream - return empty stream
  http.get('/api/sse/stream', () => {
    return HttpResponse.text('data: []\n\n', {
      headers: {
        'Content-Type': 'text/event-stream',
        'Cache-Control': 'no-cache',
        Connection: 'keep-alive',
      },
    });
  }),

  // FX rates - generate for date range
  http.get('/api/fx', ({ request }) => {
    const url = new URL(request.url);
    const base = url.searchParams.get('base') || 'GBP';
    const start = url.searchParams.get('start') || '2025-07-01';
    const end = url.searchParams.get('end') || '2026-01-15';

    return HttpResponse.json({
      base,
      rates: generateFxRates(base, start, end),
    });
  }),

  // Open banking auth link - return stub
  http.get('/api/open-banking/auth-link', () => {
    return HttpResponse.json({
      link: 'https://www.seapoint.co/',
    });
  }),

  // Evidence/invoice upload - return mock response
  http.post('/api/evidence/invoice', () => {
    return HttpResponse.json({
      evidence: {
        id: generateId('evi'),
        status: 'PROCESSING',
        createdAt: new Date().toISOString(),
      },
      document: {
        id: generateId('doc'),
        fileName: 'uploaded-invoice.pdf',
        url: '/demo/assets/invoices/sample-invoice.pdf',
      },
    });
  }),

  // Evidence upload (generic)
  http.post('/api/evidence', async ({ request }) => {
    const body = (await request.json().catch(() => ({}))) as Record<string, unknown>;
    return HttpResponse.json({
      id: generateId('evi'),
      status: 'COMPLETED',
      ...body,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  }),

  // Document signed URL - return mock URL
  http.get('/api/documents/:documentId/signed-url', ({ params }) => {
    return HttpResponse.json({
      url: `/demo/assets/documents/${params.documentId}.pdf`,
      expiresAt: new Date(Date.now() + 3600000).toISOString(),
    });
  }),

  // Email extraction status - for invoice-pay page
  http.get('/api/email/extraction-status', () => {
    return HttpResponse.json({
      status: 'completed',
      totalInvoiceCount: 0,
    });
  }),

  // Connection status - for invoice-pay page (email connections with excluded senders)
  http.get('/api/connection', () => {
    return HttpResponse.json({
      connections: [],
      totalCount: 0,
    });
  }),

  // OAuth status - all connections appear connected
  http.get('/api/oauth/status', () => {
    return HttpResponse.json({
      isConnected: true,
      provider: 'xero',
      lastSyncedAt: new Date().toISOString(),
    });
  }),

  // OAuth connections - return mock connections based on provider
  http.get('/api/oauth/connections', ({ request }: { request: Request }) => {
    const url = new URL(request.url);
    const provider = url.searchParams.get('provider');

    // Return empty connections (not connected) for demo
    if (provider === 'gmail') {
      return HttpResponse.json({
        connections: [],
        totalCount: 0,
        authUrl: 'https://accounts.google.com/oauth/authorize?demo=true',
      });
    }

    if (provider === 'xero') {
      return HttpResponse.json({
        connections: [],
        totalCount: 0,
        authUrl: 'https://login.xero.com/identity/connect/authorize?demo=true',
      });
    }

    // Default return empty connections
    return HttpResponse.json({
      connections: [],
      totalCount: 0,
    });
  }),

  // Terms and conditions
  http.get('/api/terms-and-conditions/:country/latest', ({ params }) => {
    return HttpResponse.json({
      id: 'tc_demo_latest',
      country: params.country,
      version: '2.0',
      content: 'Demo terms and conditions...',
      effectiveDate: '2025-01-01',
    });
  }),

  // Payment initiation - mock success
  http.post('/api/payments', async ({ request }) => {
    const body = (await request.json().catch(() => ({}))) as Record<string, unknown>;
    return HttpResponse.json({
      id: generateId('pay'),
      status: 'PENDING',
      authorizationStatus: 'AWAITING_AUTHORIZATION',
      ...body,
      createdAt: new Date().toISOString(),
    });
  }),

  // Payment auth callback
  http.get('/api/payments/auth/callback', () => {
    return HttpResponse.json({
      success: true,
      status: 'AUTHORIZED',
    });
  }),

  // Bulk payment - mock success
  http.post('/api/bulk-payments', async ({ request }) => {
    const body = (await request.json().catch(() => ({}))) as Record<string, unknown>;
    return HttpResponse.json({
      bulkPayment: {
        id: generateId('bpay'),
        status: 'PROCESSING',
        totalAmount: body.totalAmount,
        createdAt: new Date().toISOString(),
      },
      payments: [],
    });
  }),

  // Company sync status
  http.get('/api/companies/:companyId/sync/status', () => {
    return HttpResponse.json({
      status: 'SYNCED',
      lastSyncedAt: new Date().toISOString(),
      accounts: {
        total: 2,
        synced: 2,
        pending: 0,
        failed: 0,
      },
    });
  }),

  // Account sync - mock success
  http.post('/api/accounts/:accountId/sync', () => {
    return HttpResponse.json({
      message: 'Sync initiated',
      status: 'SYNCING',
    });
  }),

  // Company accounts sync
  http.post('/api/companies/:companyId/accounts/sync', () => {
    return HttpResponse.json({
      message: 'Account sync enqueued',
    });
  }),

  // Register company search
  http.get('/api/register/companies', ({ request }) => {
    const url = new URL(request.url);
    const query = url.searchParams.get('q') || '';

    return HttpResponse.json([
      {
        name: `${query} Ltd`,
        registrationNo: '12345678',
        country: 'GB',
        type: 'LTD - Private Company Limited by Shares',
        address: {
          street: '123 Business Street',
          city: 'London',
          postalCode: 'EC1A 1BB',
          country: 'United Kingdom',
        },
      },
    ]);
  }),

  // Verify payee - mock success
  http.post('/api/verify-payee', async ({ request }) => {
    const body = (await request.json().catch(() => ({}))) as Record<string, unknown>;
    return HttpResponse.json({
      id: generateId('vpc'),
      status: 'MATCH',
      matchScore: 95,
      ...body,
    });
  }),

  // Card operations - mock responses
  http.post('/api/cards/virtual', async ({ request }) => {
    const body = (await request.json().catch(() => ({}))) as Record<string, unknown>;
    return HttpResponse.json({
      id: generateId('card'),
      status: 'ACTIVE',
      ...body,
      createdAt: new Date().toISOString(),
    });
  }),

  http.post('/api/cards/secure-details-token', () => {
    return HttpResponse.json({
      token: 'demo_secure_token_' + Date.now(),
      expiresAt: new Date(Date.now() + 300000).toISOString(),
    });
  }),

  http.get('/api/open-banking/institutions', () => {
    return HttpResponse.json({
    "institutions": [
        {
          "externalId": "abn-amro-nl",
          "name": "ABN AMRO",
          "countries": ["NL", "DE", "BE"],
          "logo": "https://images.yapily.com/image/e91dfe65-ae60-4627-8717-ecee0f7075c4"
        },
        {
          "externalId": "aibbusiness",
          "name": "AIB Ireland Business",
          "countries": ["IE"],
          "logo": "https://images.yapily.com/image/364093b3-2c5a-4021-b757-e707f170d63d"
        },
        {
          "externalId": "aib",
          "name": "AIB Ireland Personal",
          "countries": ["IE"],
          "logo": "https://images.yapily.com/image/498af312-02c4-4f65-b512-6661a35d8aef"
        },
        {
          "externalId": "aibgbbusiness",
          "name": "AIB UK Business",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/126a10ad-f6c8-4360-a29a-3ac4cd8c7308"
        },
        {
          "externalId": "firsttrustbankbusiness",
          "name": "AIB UK Business - First Trust Bank",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/4932b75c-d696-4732-b8fb-36f3d7283e9f"
        },
        {
          "externalId": "aibgb",
          "name": "AIB UK Personal",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/0857b7d0-e38f-4c8c-9fdd-5efaac66e546"
        },
        {
          "externalId": "firsttrustbank",
          "name": "AIB UK Personal - First Trust Bank",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/19822174-cffa-4dcd-a31a-75278fb5a276"
        },
        {
          "externalId": "aibgb-sandbox",
          "name": "AIB UK Personal Sandbox",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/0896d227-7a3a-423c-988e-000c0af04dc1"
        },
        {
          "externalId": "aachener-bank",
          "name": "Aachener Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "abtsgmuer-bank",
          "name": "Abtsgmünder Bank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "airbus-bank",
          "name": "Airbus Bank GmbH",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/531c46a7-c270-4b25-8a9a-72b5e831a372"
        },
        {
          "externalId": "aktivbank",
          "name": "Aktivbank AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/b071943f-fcfc-4d49-835f-f30ccf52e97f"
        },
        {
          "externalId": "onlinebanking-alxinger-bank",
          "name": "Alxing-Brucker Genossenschaftsbank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/94fd66e0-8119-4ec7-9b48-3ccc4e4051e1"
        },
        {
          "externalId": "amex-ob_uk",
          "name": "Amex UK",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/5fc6d302-6c8e-47ca-9b6e-bb2d42a050bf"
        },
        {
          "externalId": "privatkuden-handel-abnk",
          "name": "BANK11",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/13bbb1df-5746-49de-aee9-f5fd575e76b4"
        },
        {
          "externalId": "bbbank-eg",
          "name": "BBBank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/f844560e-28d4-45aa-b3bd-72639c78c69d"
        },
        {
          "externalId": "servicebank-btv-bank",
          "name": "BTV Bank für Tirol und Vorarlberg AG Zweigniederlassung Deutschland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/e06b05b1-20e0-449f-ba0e-6573b0c13aba"
        },
        {
          "externalId": "bank1saar",
          "name": "Bank 1 Saar eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/3340cec5-796b-4cd0-afd8-1e50843ccfbd"
        },
        {
          "externalId": "kirche-caritas-bank",
          "name": "Bank für Kirche und Caritas",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/8bb6017f-54c8-49d1-8827-2eb94b15dd1a"
        },
        {
          "externalId": "kirche-diakonie-bank",
          "name": "Bank für Kirche und Diakonie eG - KD-Bank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/27ec7c27-4826-4fbf-8203-44fef67803c2"
        },
        {
          "externalId": "bistum-essen-bank",
          "name": "Bank im Bistum Essen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/0911a811-0040-47a2-a4af-c74055ab26a6"
        },
        {
          "externalId": "bankofireland",
          "name": "Bank of Ireland",
          "countries": ["IE"],
          "logo": "https://images.yapily.com/image/4bef3f74-0b0d-4c72-ad2c-ccdb486ad07f"
        },
        {
          "externalId": "bankofirelandbusiness",
          "name": "Bank of Ireland Business",
          "countries": ["IE"],
          "logo": "https://images.yapily.com/image/4bef3f74-0b0d-4c72-ad2c-ccdb486ad07f"
        },
        {
          "externalId": "bankofirelandbusiness_uk",
          "name": "Bank of Ireland Business UK",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/4bef3f74-0b0d-4c72-ad2c-ccdb486ad07f"
        },
        {
          "externalId": "bankofireland_uk",
          "name": "Bank of Ireland UK",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/4bef3f74-0b0d-4c72-ad2c-ccdb486ad07f"
        },
        {
          "externalId": "bankofscotlandbusiness",
          "name": "Bank of Scotland (Business)",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/dfc2ed6f-32b1-45ed-afa0-ff2791020b37"
        },
        {
          "externalId": "bankofscotland",
          "name": "Bank of Scotland (Personal)",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/a9bcb0bb-229c-4de6-ab96-df6314fd37b9"
        },
        {
          "externalId": "bankofscotlandcommercial_eu",
          "name": "Bank of Scotland EU (Commercial)",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/dfc2ed6f-32b1-45ed-afa0-ff2791020b37"
        },
        {
          "externalId": "bankofscotland-sandbox",
          "name": "Bank of Scotland Sandbox",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/a3dd4033-9179-4cb5-b2fa-2a7e25f43b46"
        },
        {
          "externalId": "bankofscotlandcommercial_uk",
          "name": "Bank of Scotland UK (Commercial)",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/dfc2ed6f-32b1-45ed-afa0-ff2791020b37"
        },
        {
          "externalId": "anton-hafner-bankhaus",
          "name": "Bankhaus Anton Hafner KG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/347b1428-39c5-40b1-8e7e-2c17e38e34df"
        },
        {
          "externalId": "bauer-aktiengesellschaft-bank",
          "name": "Bankhaus Bauer Aktiengesellschaft",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/992ad45b-0305-480d-b2bb-1b281d573a6e"
        },
        {
          "externalId": "mayer-bank",
          "name": "Bankhaus E. Mayer AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/7e654f88-0b8f-48a0-9461-c52772983864"
        },
        {
          "externalId": "ellwanger-geiger-bank",
          "name": "Bankhaus Ellwanger & Geiger AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/450f4e6a-bdda-4f5c-ab4a-4aec1679a3b0"
        },
        {
          "externalId": "gebr-martin-bank",
          "name": "Bankhaus Gebr. Martin AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/0a2a4a7a-cef1-43c1-b55e-98ff98750b8e"
        },
        {
          "externalId": "max-flessa-bank",
          "name": "Bankhaus Max Flessa KG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/db82e1fa-29f3-43be-81a5-82df5fdba829"
        },
        {
          "externalId": "rbk-rsa",
          "name": "Bankhaus RSA",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rautenschleinbank",
          "name": "Bankhaus Rautenschlein AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/22e8d651-ab2c-42e5-afa6-9969ca742233"
        },
        {
          "externalId": "werhahn-bank",
          "name": "Bankhaus Werhahn GmbH",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/c554a61f-8e59-4ddf-ae71-41e9aa3f6c86"
        },
        {
          "externalId": "barclaycard",
          "name": "Barclaycard",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/904f3769-f459-479b-b89b-4a1ba0b86774"
        },
        {
          "externalId": "barclaycard-commercial-payments",
          "name": "Barclaycard Commercial Payments",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/8f988b12-0b06-4021-8bb4-30bc49341a81"
        },
        {
          "externalId": "barclays_business",
          "name": "Barclays Business",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/674d2584-efb9-4f66-aa7a-03aae576035c"
        },
        {
          "externalId": "barclays_corporate",
          "name": "Barclays Corporate",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/b591ec82-9f24-4a2f-8ede-849adf11ad79"
        },
        {
          "externalId": "barclays",
          "name": "Barclays Personal",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/d8155160-2be4-4fb7-93cc-ba524e422cc3"
        },
        {
          "externalId": "barclays-sandbox",
          "name": "Barclays Sandbox",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/3dfbd21b-3d6c-4972-8fa6-f705abfb5feb"
        },
        {
          "externalId": "barclays_wealth",
          "name": "Barclays Wealth",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/23e9bf23-37bf-4a08-ab29-cdea6c60a239"
        },
        {
          "externalId": "bensbergerbank",
          "name": "Bensberger Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/4c77859b-1abc-47e5-86ca-294d249733ec"
        },
        {
          "externalId": "berliner-sparkasse",
          "name": "Berliner Sparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "berliner-volksbank",
          "name": "Berliner Volksbank eG Abteilung BIT",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "bernhauser-bank",
          "name": "Bernhauser Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "bsk-reichenau",
          "name": "Bezirkssparkasse Reichenau",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "bopfinger-bank",
          "name": "Bopfinger Bank Sechta-Ries eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "bordesholmer-sparkasse-ag",
          "name": "Bordesholmer Sparkasse AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "branburgerbank",
          "name": "Brandenburger Bank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "bremischevb",
          "name": "Bremische Volksbank Weser-Wümme",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "bruehlerbank",
          "name": "Brühler Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "bnheimervb",
          "name": "Budenheimer Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "cvw-privatbank",
          "name": "CVW-Privatbank AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/15c4d2bd-6185-4246-ab47-7faec7903480"
        },
        {
          "externalId": "capitalontap",
          "name": "Capital On Tap",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/8076f56b-ad55-4acd-8012-77a94fb296fc"
        },
        {
          "externalId": "capitalone_uk",
          "name": "Capital One",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/8342cdf5-129d-48e1-b18e-70b81f05c486"
        },
        {
          "externalId": "caterallen",
          "name": "Cater Allen",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/9daa5360-d662-418e-9edf-7771002876f3"
        },
        {
          "externalId": "chase_uk",
          "name": "Chase UK",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/0e349082-75cc-4a93-85cc-a2d7b7a2680a"
        },
        {
          "externalId": "chelseabuildingsociety",
          "name": "Chelsea Building Society",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/73dd6a83-e203-4070-a42d-f2b9703c2260"
        },
        {
          "externalId": "clanq_de",
          "name": "Clanq",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/702d6503-d275-401b-b973-8bacdac70cb5"
        },
        {
          "externalId": "comdirectbank",
          "name": "Comdirect bank AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d12c75a1-79ce-4451-8523-5f4e942c66bc"
        },
        {
          "externalId": "commerzbank",
          "name": "Commerzbank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/bdb3ff48-4436-45a5-827e-dc6f9ef99a69"
        },
        {
          "externalId": "consorsbank",
          "name": "Consorsbank by BNP PARIBAS",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/65fde072-49c7-4b2b-9927-56c61b540921"
        },
        {
          "externalId": "cooperativebank",
          "name": "Cooperative Bank (Business)",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/9d309d80-48dd-4b40-9810-945e978aa0c3"
        },
        {
          "externalId": "cooperative-personal",
          "name": "Cooperative Personal",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/d4e04492-b4ee-48bb-bd33-5449df9c0789"
        },
        {
          "externalId": "cooperative-smile",
          "name": "Cooperative Smile",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/2f2eb26c-ae7d-4324-b95a-e368ca243186"
        },
        {
          "externalId": "coutts",
          "name": "Coutts & Company",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/5f049447-3805-46fa-8e16-f3e89f6036c3"
        },
        {
          "externalId": "cronbank",
          "name": "Cronbank AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/4df482c2-1c61-4a4c-8bd8-b67a1d4dfe47"
        },
        {
          "externalId": "cumberlandbuildingsociety",
          "name": "Cumberland Building Society",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/a9787902-659c-4447-941a-645db9d1b8c3"
        },
        {
          "externalId": "dkb",
          "name": "DKB",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/37ebea9d-c3f7-4c27-acc1-3feb9ca4af6f"
        },
        {
          "externalId": "dkm",
          "name": "DKM Darlehnskasse Münster eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/7f205006-eb23-4544-8d20-ea35f93cf6ca"
        },
        {
          "externalId": "dzbank",
          "name": "DZ BANK AG Deutsche Zentral-Genossenschaftsbank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "danskebank_uk",
          "name": "Danske Bank",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/f387a249-720d-4bf5-95d0-5f96768dd09c"
        },
        {
          "externalId": "danskebankbusiness_ie",
          "name": "Danske Bank Business Ireland",
          "countries": ["IE"],
          "logo": "https://images.yapily.com/image/f387a249-720d-4bf5-95d0-5f96768dd09c"
        },
        {
          "externalId": "danskebankbusiness_uk",
          "name": "Danske Bank Business UK",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/f387a249-720d-4bf5-95d0-5f96768dd09c"
        },
        {
          "externalId": "dettinger-bank",
          "name": "Dettinger Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "deutschebank-pfb_de",
          "name": "Deutsche Bank AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/f24aa8d3-7b9e-45a0-99c2-b81036ec269a"
        },
        {
          "externalId": "postbank",
          "name": "Deutsche Postbank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/57c46112-2318-4c56-945b-288e826472bc"
        },
        {
          "externalId": "dvrb",
          "name": "Dithmarscher Volks- und Raiffeisenbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "donau-iller-bank",
          "name": "Donau-Iller Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "donner-reuschel",
          "name": "Donner & Reuschel AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/60043694-4861-4f00-8ada-e8853362eabe"
        },
        {
          "externalId": "dovoba",
          "name": "Dortmunder Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "edekabank-hamburg",
          "name": "EDEKABANK AG Hamburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/12c9c55d-3c1f-4c76-9d03-25e4917dca2c"
        },
        {
          "externalId": "echterdinger-bank",
          "name": "Echterdinger Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "eckernfoer-bank",
          "name": "Eckernförder Bank eG Volksbank-Raiffeisenbank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "emslandische-volksbank",
          "name": "Emsländische Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "erzgebirgssparkasse",
          "name": "Erzgebirgssparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ethikbank",
          "name": "EthikBank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/a9f97eeb-f66b-47a0-9486-3e955d887a95"
        },
        {
          "externalId": "eurocity-bank",
          "name": "Eurocity Bank AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/2aa5939f-e41d-4193-a14a-0491c80663e1"
        },
        {
          "externalId": "evangelische-bank",
          "name": "Evangelische Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/8aa8bb24-7408-46bb-a1e1-f0724b4a1060"
        },
        {
          "externalId": "evenordbank",
          "name": "Evenord-Bank eG-KG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/c20879b4-d095-461e-b338-432534dd9f13"
        },
        {
          "externalId": "finom_de",
          "name": "Finom Germany",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/c51f298a-2549-4399-9d96-a4fcbd212e9f"
        },
        {
          "externalId": "firstdirect",
          "name": "First Direct",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/bfa8938c-75f4-4304-9056-377e8b79923d"
        },
        {
          "externalId": "frankfurter-bankgesellschaft",
          "name": "Frankfurter Bankgesellschaft (Deutschland) AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/8286bf5f-a374-4e5f-bb2c-9bb90532157c"
        },
        {
          "externalId": "frankfurter-sparkasse",
          "name": "Frankfurter Sparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "frankfurter-volksbank",
          "name": "Frankfurter Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "fs-bank",
          "name": "Freisinger Bank eG Volksbank-Raiffeisenbank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "foerde-spk",
          "name": "Förde Sparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ff-aktiengesellschaft-bank",
          "name": "Fürst Fugger Privatbank Aktiengesellschaft",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "furstlich-castellsche-bank",
          "name": "Fürstlich Castell'sche Bank Credit-Casse AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/5e625de0-4426-4d2d-8d99-6810b84ad1c6"
        },
        {
          "externalId": "genobank",
          "name": "GENO BANK ESSEN eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "gls-gemeinschaftsbank",
          "name": "GLS Bank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/3cccb8f9-426e-4d60-9e04-1a47e92a9657"
        },
        {
          "externalId": "grenke-bank",
          "name": "GRENKE BANK AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/3498dc56-bb35-4839-a860-ff4a834ebd72"
        },
        {
          "externalId": "gabler-saliter-bank",
          "name": "Gabler-Saliter Bankgeschäft AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/df151ec6-c999-420b-9a0d-9227d23f3032"
        },
        {
          "externalId": "genobank-mainz",
          "name": "Genobank Mainz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "genobank-unterallgaeu",
          "name": "Genossenschaftsbank Unterallgäu eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "genoba-weil",
          "name": "Genossenschaftsbank Weil im Schönbuch eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "genobamuc",
          "name": "Genossenschaftsbank eG München",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "gladbacher-bank",
          "name": "Gladbacher Bank AG von 1922",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "grafschafter-volksbank",
          "name": "Grafschafter Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "grover_de",
          "name": "Grover",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/9f8bb3a4-dbf3-4ceb-997e-95537d726632"
        },
        {
          "externalId": "hsbckinetic",
          "name": "HSBC Kinetic",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/fc734d3c-4480-471e-94ee-500f83ae3bae"
        },
        {
          "externalId": "hsbccorporate",
          "name": "HSBC Net",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/fc734d3c-4480-471e-94ee-500f83ae3bae"
        },
        {
          "externalId": "hsbccorporate_eu",
          "name": "HSBC Net EU",
          "countries": ["FR", "DE", "IE", "CZ", "MT", "NL", "BE", "GR", "ES", "IT"],
          "logo": "https://images.yapily.com/image/fc734d3c-4480-471e-94ee-500f83ae3bae"
        },
        {
          "externalId": "hsbc_uk",
          "name": "HSBC Personal",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/fc734d3c-4480-471e-94ee-500f83ae3bae"
        },
        {
          "externalId": "hsbcbusiness_uk",
          "name": "HSBC UK Business",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/da33b95f-edfd-49ae-a904-f760d85ecf40"
        },
        {
          "externalId": "hagnauer-volksbank",
          "name": "Hagnauer Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "halifax",
          "name": "Halifax Personal",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/9c5470ca-cb71-4dab-ba4a-c15603d3bcf7"
        },
        {
          "externalId": "hamburger-sparkasse",
          "name": "Hamburger Sparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "hamburger-volksbank",
          "name": "Hamburger Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "handelsbankenbusiness_uk",
          "name": "Handelsbanken Business UK",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/a722b3b6-7cb9-42b6-810a-5a33d0184ede"
        },
        {
          "externalId": "handelsbanken_uk",
          "name": "Handelsbanken UK",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/dd5d8c50-73b9-4bdf-88a1-f59c53d9a82b"
        },
        {
          "externalId": "hannoversche-volksbank",
          "name": "Hannoversche Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "harzer-volksbank",
          "name": "Harzer Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "harzsparkasse",
          "name": "Harzsparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "hausbank-munchen-haus-grundbesitz-bank",
          "name": "Hausbank München",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/681c7ac8-427b-4511-ad35-3dbea4cbd67e"
        },
        {
          "externalId": "helberger-volksbank",
          "name": "Heidelberger Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "hdh-voba",
          "name": "Heidenheimer Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "helios_de",
          "name": "Helios",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/4141170a-0cdd-4597-8bb7-49d407a0b7bc"
        },
        {
          "externalId": "spk-herne",
          "name": "Herner Sparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "hoaresbank",
          "name": "Hoares Bank",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/9917712f-f3b4-44fc-92b0-2d7fe71b9b1e"
        },
        {
          "externalId": "ebanking-hoernerbank",
          "name": "Hoerner Bank AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/0df6702c-703b-4db1-9aca-5a30a9f2750b"
        },
        {
          "externalId": "unicredit_de",
          "name": "HypoVereinsbank (HVB Online Banking)",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/e2514a50-869a-469d-98cb-bc1ea5debf3c"
        },
        {
          "externalId": "huemmlinger-volksbank",
          "name": "Hümmlinger Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "huettenberger-bank",
          "name": "Hüttenberger Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "ibm-deutsche-kreditbank",
          "name": "IBM Deutschland Kreditbank GmbH",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/b2b1f3d4-a990-4066-89be-4cba973c33b6"
        },
        {
          "externalId": "ing",
          "name": "ING",
          "countries": ["FR", "DE", "IE", "CZ", "BG", "NL", "IT", "HU", "PT", "AT", "RO", "GB", "ES", "BE"],
          "logo": "https://images.yapily.com/image/115742bf-0e4e-49d9-ad86-df5afc385c00"
        },
        {
          "externalId": "ikano-zweigniederlassung-bank",
          "name": "Ikano Bank AB (publ) Zweigniederlassung Deutschland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/6782dac3-719e-40c0-9377-a8ee964a5617"
        },
        {
          "externalId": "onlinebanking-ibb-ag",
          "name": "Internationales Bankhaus Bodensee AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/8280a74d-5edf-49d2-940e-5a39883feca2"
        },
        {
          "externalId": "jtbank",
          "name": "J&T BANKA",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/06d877f0-1b0b-4ba5-b4b5-e612528ac1ba"
        },
        {
          "externalId": "ksk-syke",
          "name": "KSK Diepholz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-stendal",
          "name": "KSK Stendal",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "kasseler-sparkasse",
          "name": "Kasseler Sparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "kieler-volksbank",
          "name": "Kieler Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "kontist_de",
          "name": "Kontist",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d7fd96df-6763-400f-8eab-ba9947c42cb0"
        },
        {
          "externalId": "kssk-wasserburg",
          "name": "Kreis- und Stadtsparkasse Wasserburg am Inn",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-ahrweiler",
          "name": "Kreissparkasse Ahrweiler",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-anhalt-bitterfeld",
          "name": "Kreissparkasse Anhalt-Bitterfeld",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-bautzen",
          "name": "Kreissparkasse Bautzen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-bersenbrueck",
          "name": "Kreissparkasse Bersenbrück",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-biberach",
          "name": "Kreissparkasse Biberach",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-birkenfeld",
          "name": "Kreissparkasse Birkenfeld",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-bitburg-pruem",
          "name": "Kreissparkasse Bitburg-Prüm",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-boeblingen",
          "name": "Kreissparkasse Böblingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-boerde",
          "name": "Kreissparkasse Börde",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-doebeln",
          "name": "Kreissparkasse Döbeln",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-duesseldorf",
          "name": "Kreissparkasse Düsseldorf",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-eichsfeld",
          "name": "Kreissparkasse Eichsfeld",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-esslingen-nuertingen",
          "name": "Kreissparkasse Esslingen-Nürtingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-euskirchen",
          "name": "Kreissparkasse Euskirchen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-freudenstadt",
          "name": "Kreissparkasse Freudenstadt",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-gelnhausen",
          "name": "Kreissparkasse Gelnhausen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-gotha",
          "name": "Kreissparkasse Gotha",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-nordhorn",
          "name": "Kreissparkasse Grafschaft Bentheim zu Nordhorn",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-gross-gerau",
          "name": "Kreissparkasse Groß-Gerau",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-goeppingen",
          "name": "Kreissparkasse Göppingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-wiedenbrueck",
          "name": "Kreissparkasse Halle-Wiedenbrück",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-heidenheim",
          "name": "Kreissparkasse Heidenheim",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-heilbronn",
          "name": "Kreissparkasse Heilbronn",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-heinsberg",
          "name": "Kreissparkasse Heinsberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-herzogtum-lauenburg",
          "name": "Kreissparkasse Herzogtum Lauenburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-hildburghausen",
          "name": "Kreissparkasse Hildburghausen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-kaiserslautern",
          "name": "Kreissparkasse Kaiserslautern",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-kelheim",
          "name": "Kreissparkasse Kelheim",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-kusel",
          "name": "Kreissparkasse Kusel",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-koeln",
          "name": "Kreissparkasse Köln",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-limburg",
          "name": "Kreissparkasse Limburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-ludwigsburg",
          "name": "Kreissparkasse Ludwigsburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-mayen",
          "name": "Kreissparkasse Mayen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-melle",
          "name": "Kreissparkasse Melle",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-miesbach-tegernsee",
          "name": "Kreissparkasse Miesbach-Tegernsee",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-maerkisch-oderland",
          "name": "Kreissparkasse Märkisch-Oderland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-muenchen-starnberg-ebersberg",
          "name": "Kreissparkasse München Starnberg Ebersberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-nordhausen",
          "name": "Kreissparkasse Nordhausen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-northeim",
          "name": "Kreissparkasse Northeim",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-ostalb",
          "name": "Kreissparkasse Ostalb",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-ravensburg",
          "name": "Kreissparkasse Ravensburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-reutlingen",
          "name": "Kreissparkasse Reutlingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-rhein-hunsrueck",
          "name": "Kreissparkasse Rhein-Hunsrück",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-rottweil",
          "name": "Kreissparkasse Rottweil",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-saale-orla",
          "name": "Kreissparkasse Saale-Orla",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-saalfeld-rudolstadt",
          "name": "Kreissparkasse Saalfeld-Rudolstadt",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-saarlouis",
          "name": "Kreissparkasse Saarlouis",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-saarpfalz",
          "name": "Kreissparkasse Saarpfalz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-schluechtern",
          "name": "Kreissparkasse Schlüchtern",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-schwalm-eder",
          "name": "Kreissparkasse Schwalm-Eder",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-sigmaringen",
          "name": "Kreissparkasse Sigmaringen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-soltau",
          "name": "Kreissparkasse Soltau",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-st-wendel",
          "name": "Kreissparkasse St. Wendel",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-stade",
          "name": "Kreissparkasse Stade",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-steinfurt",
          "name": "Kreissparkasse Steinfurt",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-traunstein-trostberg",
          "name": "Kreissparkasse Traunstein-Trostberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-tuttlingen",
          "name": "Kreissparkasse Tuttlingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-tuebingen",
          "name": "Kreissparkasse Tübingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-verden",
          "name": "Kreissparkasse Verden",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-vulkaneifel",
          "name": "Kreissparkasse Vulkaneifel",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-waiblingen",
          "name": "Kreissparkasse Waiblingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-walsrode",
          "name": "Kreissparkasse Walsrode",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ksk-weilburg",
          "name": "Kreissparkasse Weilburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "banking-kurhessische-landbank",
          "name": "Kurhessische Landbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/40ea51c2-50ec-48d6-b029-34f3155b5926"
        },
        {
          "externalId": "kyffhaeusersparkasse-artern-sondershausen",
          "name": "Kyffhäusersparkasse Artern-Sondershausen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "liga-bank",
          "name": "LIGA Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/ad6d1fe2-a03e-4ad4-a47d-50467fb98d12"
        },
        {
          "externalId": "landbank-horlofftal",
          "name": "Landbank Horlofftal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "bw-bank",
          "name": "Landesbank Baden-Württemberg ",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/6bfebfe1-55f5-48d9-ad5f-3625ff93b1b9"
        },
        {
          "externalId": "lsk-oldenburg",
          "name": "Landessparkasse zu Oldenburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "onlinebanking-rentenbank",
          "name": "Landwirtschaftliche Rentenbank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "leipziger-volksbank",
          "name": "Leipziger Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "lloydsbusiness",
          "name": "Lloyds (Business)",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/687ddef5-dd48-476a-b9a7-38e294431201"
        },
        {
          "externalId": "lloyds",
          "name": "Lloyds (Personal)",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/0b0567cd-1fe6-4ff2-a82e-da53361b6fef"
        },
        {
          "externalId": "lloydscommercial_eu",
          "name": "Lloyds EU (Commercial)",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/687ddef5-dd48-476a-b9a7-38e294431201"
        },
        {
          "externalId": "lloydscommercial_uk",
          "name": "Lloyds UK (Commercial)",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/687ddef5-dd48-476a-b9a7-38e294431201"
        },
        {
          "externalId": "msbank",
          "name": "M&S Bank",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/9932ddb9-0457-4c86-8f87-ccf1a1420ff1"
        },
        {
          "externalId": "mbna",
          "name": "MBNA Personal",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/ec7cea33-700a-45fe-a819-558cb7908426"
        },
        {
          "externalId": "merkur-privatbank",
          "name": "MERKUR PRIVATBANK KGaA",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/eb26a698-3bce-4553-8d69-53bb56496907"
        },
        {
          "externalId": "mkb-mittelstandskreditbank",
          "name": "MKB Mittelstandskreditbank AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/9f32b8ae-2bce-44c4-968b-bcb40417cbe1"
        },
        {
          "externalId": "mlp-finanzdienstleistungen",
          "name": "MLP Finanzdienstleistungen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/27524550-b2d9-4101-a1be-7db10b000392"
        },
        {
          "externalId": "mvb",
          "name": "Mainzer Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "mener-bank",
          "name": "Mendener Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "metro",
          "name": "Metro Bank",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/9c06321d-c54a-457f-9222-6f39e32feed8"
        },
        {
          "externalId": "mettle",
          "name": "Mettle",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/f3fa5555-d9b6-40cb-be4a-470b15e014c2"
        },
        {
          "externalId": "mittelbrandenburgische-spk-in-potsdam",
          "name": "Mittelbrandenburgische Sparkasse in Potsdam",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "mock-berlingroup-embedded",
          "name": "Mock ASPSP Berlin Group (Embedded)",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/9e2b2cac-8a3f-48a5-9221-8b99a61b659e"
        },
        {
          "externalId": "mock-berlingroup",
          "name": "Mock ASPSP Berlin Group (Redirect)",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/9e2b2cac-8a3f-48a5-9221-8b99a61b659e"
        },
        {
          "externalId": "modelo-sandbox",
          "name": "Modelo Sandbox",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/ce2bfdbf-1ae2-4919-ab7b-e8b3d5e93b36"
        },
        {
          "externalId": "monzo_ob",
          "name": "Monzo",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/f70dc041-c7a5-47d3-9c6b-846778eac01a"
        },
        {
          "externalId": "maerkische-bank",
          "name": "Märkische Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "muenchner-bank",
          "name": "Münchner Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "mueritz-sparkasse",
          "name": "Müritz-Sparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "n26",
          "name": "N26",
          "countries": [
            "ES",
            "LI",
            "SE",
            "US",
            "FR",
            "DE",
            "BE",
            "IS",
            "NO",
            "CH",
            "IT",
            "NL",
            "DK",
            "IE",
            "GR",
            "AT",
            "FI",
            "EE"
          ],
          "logo": "https://images.yapily.com/image/2f862928-0514-4bd1-9314-d05db56d392b"
        },
        {
          "externalId": "nord-lb",
          "name": "NORD/LB",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/09df6191-9ce6-44c0-b59b-5d009b928262"
        },
        {
          "externalId": "nassauische-sparkasse",
          "name": "Nassauische Sparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "natwest-clearspend",
          "name": "NatWest ClearSpend",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/6c52c612-cc74-4a12-98fa-e9da464f59c6"
        },
        {
          "externalId": "nationwide",
          "name": "Nationwide",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/5759e5a2-f537-49e7-b8a6-9eaa293fbad8"
        },
        {
          "externalId": "natwest",
          "name": "Natwest",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/06a3822f-2890-4055-b1a1-d352532b6340"
        },
        {
          "externalId": "natwest-sandbox",
          "name": "Natwest Bank Sandbox",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/4c28e551-1a56-4631-be2c-260105ba687b"
        },
        {
          "externalId": "natwestbusiness",
          "name": "Natwest Bankline",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/26d650e9-591e-41f8-ad97-d1c49566e634"
        },
        {
          "externalId": "natwest-sandbox-vrp",
          "name": "Natwest VRP Sandbox",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/4c28e551-1a56-4631-be2c-260105ba687b"
        },
        {
          "externalId": "niederrheinische-sparkasse-rheinlippe",
          "name": "Niederrheinische Sparkasse RheinLippe",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "nord-ostsee-spk",
          "name": "Nord-Ostsee Sparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "north-channel-bank",
          "name": "North Channel Bank GmbH & Co. KG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d60091ea-4051-40f5-b40f-49fce688ec09"
        },
        {
          "externalId": "volksbank-onburg",
          "name": "Oldenburger Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "onstmettinger-bank",
          "name": "Onstmettinger Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "ostfriesische-volksbank",
          "name": "Ostfriesische Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "ostsaechsische-sparkasse-dresden",
          "name": "Ostsächsische Sparkasse Dresden",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ostseesparkasse-rostock",
          "name": "OstseeSparkasse Rostock",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "otto-m-schroder-bank",
          "name": "Otto M. Schröder Bank AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/ba3e6d76-8942-4ef8-aaeb-621e608471c9"
        },
        {
          "externalId": "psd-berlin-branburg",
          "name": "PSD Bank Berlin-Brandenburg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d93d12f8-3472-4e73-b8bc-845f7e448df4"
        },
        {
          "externalId": "onlinebanking-psd-braunschweig",
          "name": "PSD Bank Braunschweig eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d93d12f8-3472-4e73-b8bc-845f7e448df4"
        },
        {
          "externalId": "onlinebanking-psd-hannover",
          "name": "PSD Bank Hannover eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d93d12f8-3472-4e73-b8bc-845f7e448df4"
        },
        {
          "externalId": "onlinebanking-psdbank-ht",
          "name": "PSD Bank Hessen-Thüringen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d93d12f8-3472-4e73-b8bc-845f7e448df4"
        },
        {
          "externalId": "onlinebanking-psd-karlsruhe-neustadt",
          "name": "PSD Bank Karlsruhe-Neustadt eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d93d12f8-3472-4e73-b8bc-845f7e448df4"
        },
        {
          "externalId": "onlinebanking-psd-koblenz",
          "name": "PSD Bank Koblenz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d93d12f8-3472-4e73-b8bc-845f7e448df4"
        },
        {
          "externalId": "onlinebanking-psd-muenchen",
          "name": "PSD Bank München eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d93d12f8-3472-4e73-b8bc-845f7e448df4"
        },
        {
          "externalId": "onlinebanking-psd-nord",
          "name": "PSD Bank Nord eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d93d12f8-3472-4e73-b8bc-845f7e448df4"
        },
        {
          "externalId": "psd-nuernberg",
          "name": "PSD Bank Nürnberg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d93d12f8-3472-4e73-b8bc-845f7e448df4"
        },
        {
          "externalId": "onlinebanking-psd-rhein-ruhr",
          "name": "PSD Bank Rhein-Ruhr eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d93d12f8-3472-4e73-b8bc-845f7e448df4"
        },
        {
          "externalId": "onlinebanking-psd-rheinneckarsaar",
          "name": "PSD Bank RheinNeckarSaar eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d93d12f8-3472-4e73-b8bc-845f7e448df4"
        },
        {
          "externalId": "onlinebanking-psd-west",
          "name": "PSD Bank West eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d93d12f8-3472-4e73-b8bc-845f7e448df4"
        },
        {
          "externalId": "onlinebanking-psd-westfalen-lippe",
          "name": "PSD Bank Westfalen-Lippe eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "pax-kirche-caritas-bank",
          "name": "Pax-Bank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/0aacbf07-f50c-48e7-a2ef-0dc0ad5004b3"
        },
        {
          "externalId": "paypal",
          "name": "PayPal",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/d4909b7f-8dd4-4a9d-b2b4-e4ab19a48d82"
        },
        {
          "externalId": "paypal_eu",
          "name": "PayPal EU",
          "countries": [
            "ES",
            "LI",
            "MT",
            "DE",
            "NL",
            "DK",
            "CZ",
            "HU",
            "SE",
            "EE",
            "FR",
            "AT",
            "BE",
            "LT",
            "GR",
            "LV",
            "IT",
            "RO",
            "PT",
            "IE",
            "IS",
            "NO",
            "BG",
            "FI"
          ],
          "logo": "https://images.yapily.com/image/d01a8e36-27ac-48be-af9e-3ba773574956"
        },
        {
          "externalId": "payoneer",
          "name": "Payoneer - EU",
          "countries": [
            "PL",
            "CZ",
            "LI",
            "FR",
            "GR",
            "ES",
            "PT",
            "DK",
            "EE",
            "FI",
            "DE",
            "IE",
            "BG",
            "LT",
            "GI",
            "IS",
            "LV",
            "IT",
            "RO",
            "MT",
            "BE",
            "NO",
            "SE",
            "AT",
            "HU",
            "NL"
          ],
          "logo": "https://images.yapily.com/image/c8378ed7-7a7e-4272-a108-7328ea08d8fd"
        },
        {
          "externalId": "payoneer_uk",
          "name": "Payoneer - UK",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/75394850-315d-4dd9-b97c-e3fea2608b68"
        },
        {
          "externalId": "permanent-tsb",
          "name": "Permanent TSB",
          "countries": ["IE"],
          "logo": "https://images.yapily.com/image/bfbbe90f-d264-42ed-a476-44775a7c0fce"
        },
        {
          "externalId": "qonto",
          "name": "Qonto",
          "countries": ["ES", "FR", "IT", "DE"],
          "logo": "https://images.yapily.com/image/d3e296ea-1ab2-42ab-9a13-f4a8d264c3f9"
        },
        {
          "externalId": "rbsinternational-clearspend",
          "name": "RBS International ClearSpend",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/661c8501-d393-41e6-865f-bec845e43e9e"
        },
        {
          "externalId": "rsb-retail-service-bank",
          "name": "RSB Retail+Service Bank GmbH",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/f499be60-a267-4409-a025-3a5850f103e0"
        },
        {
          "externalId": "rmbeg",
          "name": "Raiffeisen - meine Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-lauf",
          "name": "Raiffeisen Spar+Kreditbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-eschweiler",
          "name": "Raiffeisen-Bank Eschweiler eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rvb-bad-staffelstein",
          "name": "Raiffeisen-Volksbank Bad Staffelstein eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rvb-donauwoerth",
          "name": "Raiffeisen-Volksbank Donauwörth eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rv-ebe",
          "name": "Raiffeisen-Volksbank Ebersberg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rvbfresena",
          "name": "Raiffeisen-Volksbank Fresena eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rvb-hassberge",
          "name": "Raiffeisen-Volksbank Haßberge eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "hermsdorfer-kreuz",
          "name": "Raiffeisen-Volksbank Hermsdorfer Kreuz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisen-volksbank-neustadt",
          "name": "Raiffeisen-Volksbank Neustadt eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rvbrspree",
          "name": "Raiffeisen-Volksbank Oder-Spree eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rvbankries",
          "name": "Raiffeisen-Volksbank Ries eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rv-banken",
          "name": "Raiffeisen-Volksbank Tüssling-Unterneukirchen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rvbvarelnonham",
          "name": "Raiffeisen-Volksbank Varel - Nordenham eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rvbwemding",
          "name": "Raiffeisen-Volksbank Wemding eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "meine-rvb",
          "name": "Raiffeisen-Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raibadirekt",
          "name": "Raiffeisenbank Aichhalden-Hardt- Sulgen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "ihrziel",
          "name": "Raiffeisenbank Aidlingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-aindling",
          "name": "Raiffeisenbank Aindling eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-aitrang-rud",
          "name": "Raiffeisenbank Aitrang-Ruderatshofen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-ah",
          "name": "Raiffeisenbank Alteglofsheim-Hagelstadt eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-gretha",
          "name": "Raiffeisenbank Altmühl-Jura eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-altschweier",
          "name": "Raiffeisenbank Altschweier eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-anger",
          "name": "Raiffeisenbank Anger eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-arge",
          "name": "Raiffeisenbank Aresing-Gerolsbach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-arnstorf",
          "name": "Raiffeisenbank Arnstorf eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-as",
          "name": "Raiffeisenbank Aschau-Samerberg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-af",
          "name": "Raiffeisenbank Auerbach-Freihung eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-alw",
          "name": "Raiffeisenbank Augsburger Land West eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-koetzting",
          "name": "Raiffeisenbank Bad Kötzting eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-bad-saulgau",
          "name": "Raiffeisenbank Bad Saulgau eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbbs",
          "name": "Raiffeisenbank Bad Schussenried-Aulendorf eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-bw",
          "name": "Raiffeisenbank Bad Windsheim eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisen-privatbank",
          "name": "Raiffeisenbank Baiertal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-eggenthal",
          "name": "Raiffeisenbank Baisweil-Eggenthal-Friesenried eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-bechhofen",
          "name": "Raiffeisenbank Bechhofen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-berghuelen",
          "name": "Raiffeisenbank Berghülen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-beuerberg",
          "name": "Raiffeisenbank Beuerberg-Eurasburg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-bidingen",
          "name": "Raiffeisenbank Bidingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-biebergrund-petersberg",
          "name": "Raiffeisenbank Biebergrund-Petersberg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-bobingen",
          "name": "Raiffeisenbank Bobingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-eching",
          "name": "Raiffeisenbank Buch-Eching eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-bs",
          "name": "Raiffeisenbank Burgebrach-Stegaurach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbbutjadingenabbehausen",
          "name": "Raiffeisenbank Butjadingen- Abbehausen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-boellingertal",
          "name": "Raiffeisenbank Böllingertal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-buehlertal",
          "name": "Raiffeisenbank Bühlertal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-buett-gauk",
          "name": "Raiffeisenbank Bütthard-Gaukönigshofen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-chamer-land",
          "name": "Raiffeisenbank Chamer Land eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-chiemgau-nord",
          "name": "Raiffeisenbank Chiemgau-Nord-Obing eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbnzlingen-sexau",
          "name": "Raiffeisenbank Denzlingen-Sexau eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-dietersheim",
          "name": "Raiffeisenbank Dietersheim und Umgebung eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-donau-heuberg",
          "name": "Raiffeisenbank Donau-Heuberg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "seebachgrund",
          "name": "Raiffeisenbank DreiFranken eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-ehekirchen",
          "name": "Raiffeisenbank Ehekirchen-Oberhausen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-simmerath",
          "name": "Raiffeisenbank Eifel eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbelbmarsch",
          "name": "Raiffeisenbank Elbmarsch eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-elsavatal",
          "name": "Raiffeisenbank Elsavatal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "emsland-mitte",
          "name": "Raiffeisenbank Ems-Vechte eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-erlenbach",
          "name": "Raiffeisenbank Erlenbach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbankersingen",
          "name": "Raiffeisenbank Ersingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-elln",
          "name": "Raiffeisenbank Eschlkam-Lam- Lohberg-Neukirchen b. Hl. Blut eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbfalkenstein-woerth",
          "name": "Raiffeisenbank Falkenstein-Wörth eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-flachsmeer",
          "name": "Raiffeisenbank Flachsmeer eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-floss",
          "name": "Raiffeisenbank Floß eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-frankenhardt-stimpfach",
          "name": "Raiffeisenbank Frankenhardt-Stimpfach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-frs",
          "name": "Raiffeisenbank Fränkische Schweiz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-geiselhoering",
          "name": "Raiffeisenbank Geiselhöring-Pfaffenberg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-gr",
          "name": "Raiffeisenbank Geislingen-Rosenfeld eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-gilching",
          "name": "Raiffeisenbank Gilching eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-gmund",
          "name": "Raiffeisenbank Gmund am Tegernsee eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-ags",
          "name": "Raiffeisenbank Goldener Steig-Dreisessel",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-grainet",
          "name": "Raiffeisenbank Grainet eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-gh",
          "name": "Raiffeisenbank Griesstätt-Halfing eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-grimma",
          "name": "Raiffeisenbank Grimma eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-gruibingen",
          "name": "Raiffeisenbank Gruibingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-graevenwiesbach",
          "name": "Raiffeisenbank Grävenwiesbach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-gymnich",
          "name": "Raiffeisenbank Gymnich eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbk-haag-gars",
          "name": "Raiffeisenbank Haag-Gars-Maitenbeth eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raibahallertau",
          "name": "Raiffeisenbank Hallertau eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-hardt-bruhrain",
          "name": "Raiffeisenbank Hardt-Bruhrain eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-hs",
          "name": "Raiffeisenbank Hengersberg-Schöllnach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-hessennord",
          "name": "Raiffeisenbank HessenNord eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "onlinebanking-raiffeisenbank-hiltenfingen",
          "name": "Raiffeisenbank Hiltenfingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-hirschau",
          "name": "Raiffeisenbank Hirschau eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-hfw",
          "name": "Raiffeisenbank Hochfranken West eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-hl",
          "name": "Raiffeisenbank Hohenloher Land eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-holzkirchen-otterfing",
          "name": "Raiffeisenbank Holzkirchen-Otterfing eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-hoechberg",
          "name": "Raiffeisenbank Höchberg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-ichenhausen",
          "name": "Raiffeisenbank Ichenhausen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rileg",
          "name": "Raiffeisenbank Isar-Loisachtal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-kaarst",
          "name": "Raiffeisenbank Kaarst eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-kaiserstuhl",
          "name": "Raiffeisenbank Kaiserstuhl eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbkalbe-bismark",
          "name": "Raiffeisenbank Kalbe-Bismark eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-kastellaun",
          "name": "Raiffeisenbank Kastellaun eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-kieselbronn",
          "name": "Raiffeisenbank Kieselbronn eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-kirchweihtal",
          "name": "Raiffeisenbank Kirchweihtal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-kirtorf",
          "name": "Raiffeisenbank Kirtorf eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-bibertgrund",
          "name": "Raiffeisenbank Knoblauchsland-Bibertgrund",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbkk",
          "name": "Raiffeisenbank Kreis Kelheim eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-kms",
          "name": "Raiffeisenbank Küps-Mitwitz-Stockheim eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-altdorf-ergolding",
          "name": "Raiffeisenbank Landshuter Land eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-nordkreis",
          "name": "Raiffeisenbank Lechrain eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-lorup",
          "name": "Raiffeisenbank Lorup eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-eifeltor",
          "name": "Raiffeisenbank MEHR",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-msp",
          "name": "Raiffeisenbank Main-Spessart eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbvw",
          "name": "Raiffeisenbank Mainschleife - Steigerwald eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-maitis",
          "name": "Raiffeisenbank Maitis eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-seenplatte",
          "name": "Raiffeisenbank Mecklenburger Seenplatte eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbmehring-leiwen",
          "name": "Raiffeisenbank Mehring-Leiwen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-mittelschwaben",
          "name": "Raiffeisenbank Mittelschwaben eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-mittenwald",
          "name": "Raiffeisenbank Mittenwald eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "banking-rb-mnord",
          "name": "Raiffeisenbank München-Nord eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-munchen",
          "name": "Raiffeisenbank München-Süd eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-nahe",
          "name": "Raiffeisenbank Nahe eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-neumarkt-opf",
          "name": "Raiffeisenbank Neumarkt i.d.OPf. eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-nr",
          "name": "Raiffeisenbank Neumarkt- St. Veit - Reischach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-neustadt",
          "name": "Raiffeisenbank Neustadt eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-nire-alb",
          "name": "Raiffeisenbank Niedere Alb eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-ndwa",
          "name": "Raiffeisenbank Niederwallmenach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-alsbach",
          "name": "Raiffeisenbank Nördliche Bergstraße eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-oberaudorf",
          "name": "Raiffeisenbank Oberaudorf eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-burgthann",
          "name": "Raiffeisenbank Oberferrieden-Burgthann eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-oberland",
          "name": "Raiffeisenbank Oberland eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbobermain",
          "name": "Raiffeisenbank Obermain Nord eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-onw",
          "name": "Raiffeisenbank Oberpfalz NordWest eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-os",
          "name": "Raiffeisenbank Oberpfalz Süd eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-om",
          "name": "Raiffeisenbank Oberteuringen-Meckenbeuren eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbok",
          "name": "Raiffeisenbank Ortenburg-Kirchberg v.W. eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbopr",
          "name": "Raiffeisenbank Ostprignitz - Ruppin eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-ottenbach",
          "name": "Raiffeisenbank Ottenbach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-parkstetten",
          "name": "Raiffeisenbank Parkstetten eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-pfaffenhausen",
          "name": "Raiffeisenbank Pfaffenhausen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "onlinebanking-raiba-pfaffenhofen",
          "name": "Raiffeisenbank Pfaffenhofen a.d.Glonn eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-pfaffenwinkel",
          "name": "Raiffeisenbank Pfaffenwinkel eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "onlinebanking-rb-plankstetten",
          "name": "Raiffeisenbank Plankstetten AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-massbach",
          "name": "Raiffeisenbank Rannungen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-rast",
          "name": "Raiffeisenbank Rastede eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-rattiszell-konzell",
          "name": "Raiffeisenbank Rattiszell-Konzell eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-regensburg",
          "name": "Raiffeisenbank Regensburg-Wenzenbach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "onlinebanking-raiffeisenbank-regenstauf",
          "name": "Raiffeisenbank Regenstauf eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-rehling",
          "name": "Raiffeisenbank Rehling eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-reute-gaisbeuren",
          "name": "Raiffeisenbank Reute-Gaisbeuren eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-rosenstein",
          "name": "Raiffeisenbank Rosenstein eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-rupertiwinkel",
          "name": "Raiffeisenbank Rupertiwinkel eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-schaafheim",
          "name": "Raiffeisenbank Schaafheim eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-sobland",
          "name": "Raiffeisenbank Schrobenhausener Land eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "unsere-raiba",
          "name": "Raiffeisenbank Schrozberg- Rot am See eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-schwaben",
          "name": "Raiffeisenbank Schwaben Mitte eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-schwabmuenchen",
          "name": "Raiffeisenbank Schwabmünchen-Stauden eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-singoldtal",
          "name": "Raiffeisenbank Singoldtal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-sinzing",
          "name": "Raiffeisenbank Sinzing eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-solfingen",
          "name": "Raiffeisenbank Sondelfingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-steingn",
          "name": "Raiffeisenbank Steingaden eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-straubing",
          "name": "Raiffeisenbank Straubing eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-idafehn",
          "name": "Raiffeisenbank Strücklingen- Idafehn eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-suedhardt",
          "name": "Raiffeisenbank Südhardt eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-sued-oal",
          "name": "Raiffeisenbank Südliches Ostallgäu eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbsum",
          "name": "Raiffeisenbank Südstormarn Mölln eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-tofk",
          "name": "Raiffeisenbank Taufkirchen-Oberneukirchen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-thannhausen",
          "name": "Raiffeisenbank Thannhausen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-thurnauerland",
          "name": "Raiffeisenbank Thurnauer Land eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "onlinebanking-raiba-tuengental",
          "name": "Raiffeisenbank Tüngental eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-tuerkheim",
          "name": "Raiffeisenbank Türkheim eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbui",
          "name": "Raiffeisenbank Unteres Inntal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbuv",
          "name": "Raiffeisenbank Unteres Vilstal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-uz",
          "name": "Raiffeisenbank Unteres Zusamtal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-wald",
          "name": "Raiffeisenbank Wald-Görisried eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-wallgau-kruen",
          "name": "Raiffeisenbank Wallgau-Krün eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-wangen",
          "name": "Raiffeisenbank Wangen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-wegscheid",
          "name": "Raiffeisenbank Wegscheid eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-welling",
          "name": "Raiffeisenbank Welling eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-wl",
          "name": "Raiffeisenbank Werratal-Landeck eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raibawesermarschsued",
          "name": "Raiffeisenbank Wesermarsch-Süd eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-westeifel",
          "name": "Raiffeisenbank Westeifel eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-westhausen",
          "name": "Raiffeisenbank Westhausen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "westkreis",
          "name": "Raiffeisenbank Westkreis Fürstenfeldbruck eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raibawiesrmeer",
          "name": "Raiffeisenbank Wiesedermeer- Wiesede-Marcardsmoor eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-wittelsbacher",
          "name": "Raiffeisenbank Wittelsbacher Land eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-wyhl",
          "name": "Raiffeisenbank Wyhl eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-wuestenselbitz",
          "name": "Raiffeisenbank Wüstenselbitz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-am-kulm",
          "name": "Raiffeisenbank am Kulm eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-baunatal",
          "name": "Raiffeisenbank eG Baunatal",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "die-raiffeisenbank",
          "name": "Raiffeisenbank eG Büchen-Crivitz-Hagenow-Plate",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbggendorf",
          "name": "Raiffeisenbank eG Deggendorf-Plattling-Sonnenwald",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-elztal",
          "name": "Raiffeisenbank eG Elztal",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbscharrel",
          "name": "Raiffeisenbank eG Scharrel",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raibalauenburg",
          "name": "Raiffeisenbank eG, Lauenburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbleezen",
          "name": "Raiffeisenbank eG, Leezen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raibamol",
          "name": "Raiffeisenbank eG, Moormerland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbowschlag",
          "name": "Raiffeisenbank eG, Owschlag",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbrnbach",
          "name": "Raiffeisenbank eG, Rodenbach",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbseestermuehe",
          "name": "Raiffeisenbank eG, Seestermühe",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbtnbuettel",
          "name": "Raiffeisenbank eG, Todenbüttel",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-allgaeuerland",
          "name": "Raiffeisenbank im Allgäuer Land eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiffeisenbank-im-breisgau",
          "name": "Raiffeisenbank im Breisgau eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-idt",
          "name": "Raiffeisenbank im Donautal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-fuldaerland",
          "name": "Raiffeisenbank im Fuldaer Land eG ",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-grabfeld",
          "name": "Raiffeisenbank im Grabfeld eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raibacalw",
          "name": "Raiffeisenbank im Kreis Calw eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rbpn",
          "name": "Raiffeisenbank im Landkreis Passau-Nord eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "onlinebanking-rb-hersbruck",
          "name": "Raiffeisenbank im Nürnberger Land eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "oberlandbank",
          "name": "Raiffeisenbank im Oberland eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-opf-jura",
          "name": "Raiffeisenbank im Oberpfälzer Jura eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-ueda",
          "name": "Raiffeisenbank Ühlfeld-Dachsbach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "meinebank",
          "name": "Raiffeisenbankim Hochtaunus",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "revolut",
          "name": "Revolut",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/fdd1045e-daa8-485c-9315-87fcc2e06e6c"
        },
        {
          "externalId": "revolut_eu",
          "name": "Revolut EU",
          "countries": [
            "ES",
            "LI",
            "MT",
            "DE",
            "NL",
            "DK",
            "CZ",
            "HU",
            "SE",
            "EE",
            "FR",
            "AT",
            "BE",
            "LT",
            "GR",
            "LV",
            "IT",
            "RO",
            "PT",
            "IE",
            "IS",
            "NO",
            "BG",
            "FI"
          ],
          "logo": "https://images.yapily.com/image/fdd1045e-daa8-485c-9315-87fcc2e06e6c"
        },
        {
          "externalId": "revolut_eu-sandbox",
          "name": "Revolut EU Sandbox",
          "countries": [
            "MT",
            "EE",
            "SE",
            "NL",
            "GR",
            "FI",
            "DE",
            "IT",
            "BG",
            "BE",
            "IE",
            "IS",
            "LT",
            "CZ",
            "DK",
            "SK",
            "SI",
            "LU",
            "ES",
            "PT",
            "FR",
            "CY",
            "LI",
            "NO",
            "LV",
            "HU",
            "HR",
            "AT",
            "RO"
          ],
          "logo": "https://images.yapily.com/image/fdd1045e-daa8-485c-9315-87fcc2e06e6c"
        },
        {
          "externalId": "rheingauer-volksbank",
          "name": "Rheingauer Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rhoen-rennsteig-spk",
          "name": "Rhön-Rennsteig Sparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "rbs",
          "name": "Royal Bank of Scotland",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/694b1b08-bb78-40d8-bf58-aae12a40de99"
        },
        {
          "externalId": "rbsbusiness",
          "name": "Royal Bank of Scotland Bankline",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/23735cc4-18f6-4dbc-b67a-001ca9040c8c"
        },
        {
          "externalId": "rbs-clearspend",
          "name": "Royal Bank of Scotland ClearSpend",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/661c8501-d393-41e6-865f-bec845e43e9e"
        },
        {
          "externalId": "smbc",
          "name": "SMBC",
          "countries": ["DE", "FR", "BE"],
          "logo": "https://images.yapily.com/image/ccddb088-c0d3-40f1-88bc-e789ac79ebb7"
        },
        {
          "externalId": "smbc_uk",
          "name": "SMBC - UK",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/ccddb088-c0d3-40f1-88bc-e789ac79ebb7"
        },
        {
          "externalId": "spk-bodensee",
          "name": "SPK Bodensee",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-dortmund",
          "name": "SPK Dortmund",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-fuerstenfeldbruck",
          "name": "SPK Fürstenfeldbruck",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-gunzenhausen",
          "name": "SPK Gunzenhausen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-hannover",
          "name": "SPK Hannover",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-hochfranken",
          "name": "SPK Hochfranken",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-karlsruhe",
          "name": "SPK Karlsruhe",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "sparkasse-mitten-im-sauerland",
          "name": "SPK Mitten im Sauerland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-neunkirchen",
          "name": "SPK Neunkirchen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-oberland",
          "name": "SPK Oberland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-ostprignitz-ruppin",
          "name": "SPK Ostprignitz-Ruppin",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-pfaffenhofen",
          "name": "SPK Pfaffenhofen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-regensburg",
          "name": "SPK Regensburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-saarbruecken",
          "name": "SPK Saarbrücken",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-schwaben-bodensee",
          "name": "SPK Schwaben-Bodensee",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-muenchen",
          "name": "SSK München",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-oberhausen",
          "name": "SSK Oberhausen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "saalesparkasse",
          "name": "Saalesparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "sainsburysbank",
          "name": "Sainsbury's Bank",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/794c0539-cce3-4997-8e3e-8bd2c29f1b7a"
        },
        {
          "externalId": "salzlandsparkasse",
          "name": "Salzlandsparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "santander_uk",
          "name": "Santander",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/a32a626b-92e4-449f-843a-007ca9bb0240"
        },
        {
          "externalId": "sob-bank",
          "name": "Schrobenhausener Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "soldo-token_ie",
          "name": "Soldo Ireland",
          "countries": ["IE"],
          "logo": "https://images.yapily.com/image/fc6c714c-837f-4ba4-a4b8-c7b95884c8b6"
        },
        {
          "externalId": "soldo-token",
          "name": "Soldo UK",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/fc6c714c-837f-4ba4-a4b8-c7b95884c8b6"
        },
        {
          "externalId": "sozialwirtschaft",
          "name": "SozialBank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/14bea818-a743-4125-8182-544e5c79f3e7"
        },
        {
          "externalId": "skb-gemuen",
          "name": "Spar- u. Kredit-Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "sdk-bockum-hoevel",
          "name": "Spar- und Darlehnskasse Bockum-Hövel eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "sdkinlamstedt",
          "name": "Spar- und Darlehnskasse Börde Lamstedt-Hechthausen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "skb-buehlertal",
          "name": "Spar- und Kreditbank Bühlertal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "skb-badhomburg",
          "name": "Spar- und Kreditbank Evangelisch-Freikirchlicher Gemeinden eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "skb-rheinstetten",
          "name": "Spar- und Kreditbank Rheinstetten eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "skbwitten",
          "name": "Spar- und Kreditbank des Bundes Freier evangelischer Gemeinden eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "skb-hammah",
          "name": "Spar- und Kreditbank eG, Hammah",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "spardabankaugsburg",
          "name": "Sparda-Bank Augsburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/8a77441c-9f34-43ac-bd85-9b07e1fa80e2"
        },
        {
          "externalId": "spardabankbaden",
          "name": "Sparda-Bank Baden-Württemberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/8a77441c-9f34-43ac-bd85-9b07e1fa80e2"
        },
        {
          "externalId": "sparda-b",
          "name": "Sparda-Bank Berlin eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/c4e8fff0-6fd2-42b4-af44-3a28d3ebf141"
        },
        {
          "externalId": "sparda-bank-hamburg",
          "name": "Sparda-Bank Hamburg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/eb4a912f-c552-445a-a2ba-acf670346939"
        },
        {
          "externalId": "sparda-h",
          "name": "Sparda-Bank Hannover eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/f98803dd-cebe-443e-9947-79a1f4bc1184"
        },
        {
          "externalId": "spardabankhessen",
          "name": "Sparda-Bank Hessen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/8a77441c-9f34-43ac-bd85-9b07e1fa80e2"
        },
        {
          "externalId": "spardabankmunchen",
          "name": "Sparda-Bank München eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/8a77441c-9f34-43ac-bd85-9b07e1fa80e2"
        },
        {
          "externalId": "spardabanknurnberg",
          "name": "Sparda-Bank Nürnberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/8a77441c-9f34-43ac-bd85-9b07e1fa80e2"
        },
        {
          "externalId": "spardabankostbayern",
          "name": "Sparda-Bank Ostbayern",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/8a77441c-9f34-43ac-bd85-9b07e1fa80e2"
        },
        {
          "externalId": "sparda-sw",
          "name": "Sparda-Bank Südwest eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/5fdd1708-4b5b-427f-9d19-719c15d68613"
        },
        {
          "externalId": "spardabankwest-essen",
          "name": "Sparda-Bank West - Essen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/8a77441c-9f34-43ac-bd85-9b07e1fa80e2"
        },
        {
          "externalId": "spardabankwest-koeln",
          "name": "Sparda-Bank West - Koeln",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/8a77441c-9f34-43ac-bd85-9b07e1fa80e2"
        },
        {
          "externalId": "spardabankwest-muenster",
          "name": "Sparda-Bank West - Muenster",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/8a77441c-9f34-43ac-bd85-9b07e1fa80e2"
        },
        {
          "externalId": "spardabankwest-wuppertal",
          "name": "Sparda-Bank West - Wuppertal",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/8a77441c-9f34-43ac-bd85-9b07e1fa80e2"
        },
        {
          "externalId": "spk-aachen",
          "name": "Sparkasse Aachen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-aichach-schrobenhausen",
          "name": "Sparkasse Aichach-Schrobenhausen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-allgaeu",
          "name": "Sparkasse Allgäu",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-altenburger-land",
          "name": "Sparkasse Altenburger Land",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-altmark-west",
          "name": "Sparkasse Altmark West",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-altoetting-muehldorf",
          "name": "Sparkasse Altötting-Mühldorf",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-amberg-sulzbach",
          "name": "Sparkasse Amberg-Sulzbach",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-ansbach",
          "name": "Sparkasse Ansbach",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-arnsberg-sundern",
          "name": "Sparkasse Arnsberg-Sundern",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-arnstadt-ilmenau",
          "name": "Sparkasse Arnstadt-Ilmenau",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-aschaffenburg-alzenau",
          "name": "Sparkasse Aschaffenburg Miltenberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-attendorn-lennestadt-kirchhundem",
          "name": "Sparkasse Attendorn-Lennestadt-Kirchhundem",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-aurich-norden",
          "name": "Sparkasse Aurich-Norden",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-bad-hersfeld-rotenburg",
          "name": "Sparkasse Bad Hersfeld-Rotenburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-bad-kissingen",
          "name": "Sparkasse Bad Kissingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-bad-neustadt-ad-saale",
          "name": "Sparkasse Bad Neustadt a.d. Saale",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-bad-oeynhausen-porta-westfalica",
          "name": "Sparkasse Bad Oeynhausen - Porta Westfalica",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-bad-toelz-wolfratshausen",
          "name": "Sparkasse Bad Tölz-Wolfratshausen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-baden-baden-gaggenau",
          "name": "Sparkasse Baden-Baden Gaggenau",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-bamberg",
          "name": "Sparkasse Bamberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-barnim",
          "name": "Sparkasse Barnim",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-battenberg",
          "name": "Sparkasse Battenberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-bayreuth",
          "name": "Sparkasse Bayreuth",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-bensheim",
          "name": "Sparkasse Bensheim",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-berchtesgadener-land",
          "name": "Sparkasse Berchtesgadener Land",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-bergkamen-boenen",
          "name": "Sparkasse Bergkamen-Bönen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-bielefeld",
          "name": "Sparkasse Bielefeld",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-bochum",
          "name": "Sparkasse Bochum",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-bonndorf-stuehlingen",
          "name": "Sparkasse Bonndorf-Stühlingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-schwalmstadt",
          "name": "Sparkasse Borken-Schwalmstadt",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-bottrop",
          "name": "Sparkasse Bottrop",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-bremen",
          "name": "Sparkasse Bremen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-burbach-neunkirchen",
          "name": "Sparkasse Burbach-Neunkirchen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-burgenlandkreis",
          "name": "Sparkasse Burgenlandkreis",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-buehl",
          "name": "Sparkasse Bühl",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-chemnitz",
          "name": "Sparkasse Chemnitz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-coburg-lichtenfels",
          "name": "Sparkasse Coburg-Lichtenfels",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-dachau",
          "name": "Sparkasse Dachau",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-darmstadt",
          "name": "Sparkasse Darmstadt",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-deggendorf",
          "name": "Sparkasse Deggendorf",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-dieburg",
          "name": "Sparkasse Dieburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-dillenburg",
          "name": "Sparkasse Dillenburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "sparkasse-dillingen-noerdlingen",
          "name": "Sparkasse Dillingen-Nördlingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-donauwoerth",
          "name": "Sparkasse Donauwörth",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-donnersberg",
          "name": "Sparkasse Donnersberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-duderstadt",
          "name": "Sparkasse Duderstadt",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-duisburg",
          "name": "Sparkasse Duisburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-dueren",
          "name": "Sparkasse Düren",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-einbeck",
          "name": "Sparkasse Einbeck",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-elbe-elster",
          "name": "Sparkasse Elbe-Elster",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-elmshorn",
          "name": "Sparkasse Elmshorn",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-emden",
          "name": "Sparkasse Emden",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-emsland",
          "name": "Sparkasse Emsland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-engen-gottmadingen",
          "name": "Sparkasse Engen-Gottmadingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-erding-dorfen",
          "name": "Sparkasse Erding-Dorfen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-eschenbach-neustadt-ad-waldnaa",
          "name": "Sparkasse Eschenbach Neustadt (a.d. Waldnaab)",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-essen",
          "name": "Sparkasse Essen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-forchheim",
          "name": "Sparkasse Forchheim",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-freiburg-noerdlicher-breisgau",
          "name": "Sparkasse Freiburg-Nördlicher Breisgau",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-freising",
          "name": "Sparkasse Freising Moosburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-freyung-grafenau",
          "name": "Sparkasse Freyung-Grafenau",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-fulda",
          "name": "Sparkasse Fulda",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-fuerth",
          "name": "Sparkasse Fürth",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-gelsenkirchen",
          "name": "Sparkasse Gelsenkirchen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-gera-greiz",
          "name": "Sparkasse Gera-Greiz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-geseke",
          "name": "Sparkasse Geseke",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-gevelsberg-wetter",
          "name": "Sparkasse Gevelsberg-Wetter",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-giessen",
          "name": "Sparkasse Gießen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-gladbeck",
          "name": "Sparkasse Gladbeck",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-gruenberg",
          "name": "Sparkasse Grünberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-gummersbach",
          "name": "Sparkasse Gummersbach",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-goettingen",
          "name": "Sparkasse Göttingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-guetersloh-rietberg",
          "name": "Sparkasse Gütersloh-Rietberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-hameln-weserbergland",
          "name": "Sparkasse Hameln-Weserbergland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-hamm",
          "name": "Sparkasse Hamm",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-hanau",
          "name": "Sparkasse Hanau",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-hanauerland",
          "name": "Sparkasse Hanauerland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-harburg-buxtehude",
          "name": "Sparkasse Harburg-Buxtehude",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-hattingen",
          "name": "Sparkasse Hattingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-hegau-bodensee",
          "name": "Sparkasse Hegau-Bodensee",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-heidelberg",
          "name": "Sparkasse Heidelberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-soestwerl",
          "name": "Sparkasse Hellweg-Lippe",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-herford",
          "name": "Sparkasse Herford",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-hilden-ratingen-velbert",
          "name": "Sparkasse Hilden-Ratingen-Velbert",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-hildesheim-goslar-peine",
          "name": "Sparkasse Hildesheim Goslar Peine",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-hochrhein",
          "name": "Sparkasse Hochrhein",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-hochsauerland",
          "name": "Sparkasse Hochsauerland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-hochschwarzwald",
          "name": "Sparkasse Hochschwarzwald",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-hohenlohekreis",
          "name": "Sparkasse Hohenlohekreis",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-holstein",
          "name": "Sparkasse Holstein",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-ingolstadteichstaett",
          "name": "Sparkasse Ingolstadt Eichstätt",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-iserlohn",
          "name": "Sparkasse Iserlohn",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-jena-saale-holzland",
          "name": "Sparkasse Jena-Saale-Holzland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-kierspe-meinerzhagen",
          "name": "Sparkasse Kierspe-Meinerzhagen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-haslach-zell",
          "name": "Sparkasse Kinzigtal",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-koblenz",
          "name": "Sparkasse Koblenz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-kraichgau",
          "name": "Sparkasse Kraichgau",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-krefeld",
          "name": "Sparkasse Krefeld",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-kulmbach-kronach",
          "name": "Sparkasse Kulmbach-Kronach",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-koelnbonn",
          "name": "Sparkasse KölnBonn",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-landsberg-diessen",
          "name": "Sparkasse Landsberg-Dießen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-landshut",
          "name": "Sparkasse Landshut",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-langen-seligenstadt",
          "name": "Sparkasse Langen-Seligenstadt",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-laubach-hungen",
          "name": "Sparkasse Laubach-Hungen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-leerwittmund",
          "name": "Sparkasse LeerWittmund",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-lemgo",
          "name": "Sparkasse Lemgo",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-leverkusen",
          "name": "Sparkasse Leverkusen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-loerrach-rheinfelden",
          "name": "Sparkasse Lörrach-Rheinfelden",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-lueneburg",
          "name": "Sparkasse Lüneburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-mainfranken-wuerzburg",
          "name": "Sparkasse Mainfranken Würzburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-mansfeld-suedharz",
          "name": "Sparkasse Mansfeld-Südharz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-marburg-biedenkopf",
          "name": "Sparkasse Marburg-Biedenkopf",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-markgraeflerland",
          "name": "Sparkasse Markgräflerland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-mecklenburg-nordwest",
          "name": "Sparkasse Mecklenburg-Nordwest",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-mecklenburg-schwerin",
          "name": "Sparkasse Mecklenburg-Schwerin",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-mecklenburg-strelitz",
          "name": "Sparkasse Mecklenburg-Strelitz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-meissen",
          "name": "Sparkasse Meißen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-merzig-wadern",
          "name": "Sparkasse Merzig-Wadern",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-minden-luebbecke",
          "name": "Sparkasse Minden-Lübbecke",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-mittelfranken-sued",
          "name": "Sparkasse Mittelfranken-Süd",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-mittelholstein-ag",
          "name": "Sparkasse Mittelholstein",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-mittelmosel-eifel-mosel-hunsrueck",
          "name": "Sparkasse Mittelmosel-Eifel-Mosel-Hunsrück",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-mittelsachsen",
          "name": "Sparkasse Mittelsachsen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-mittelthueringen",
          "name": "Sparkasse Mittelthüringen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-muldental",
          "name": "Sparkasse Muldental",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-maerkisches-sauerland-hemer-menden",
          "name": "Sparkasse Märkisches Sauerland Hemer - Menden",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-muelheim-ad-ruhr",
          "name": "Sparkasse Mülheim a.d. Ruhr",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-muensterland-ost",
          "name": "Sparkasse Münsterland Ost",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-neckartal-odenwald",
          "name": "Sparkasse Neckartal-Odenwald",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-neu-ulm-illertissen",
          "name": "Sparkasse Neu-Ulm-Illertissen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-neubrandenburg-demmin",
          "name": "Sparkasse Neubrandenburg-Demmin",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-neuburg-rain",
          "name": "Sparkasse Neuburg-Rain",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-neumarkt-idopf-parsberg",
          "name": "Sparkasse Neumarkt i.d.OPf.-Parsberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-neuss",
          "name": "Sparkasse Neuss",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-neuwied",
          "name": "Sparkasse Neuwied",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-niederbayern-mitte",
          "name": "Sparkasse Niederbayern-Mitte",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-niederlausitz",
          "name": "Sparkasse Niederlausitz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-nienburg",
          "name": "Sparkasse Nienburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-nuernberg",
          "name": "Sparkasse Nürnberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-oberhessen",
          "name": "Sparkasse Oberhessen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-oberlausitz-niederschlesien",
          "name": "Sparkasse Oberlausitz-Niederschlesien",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-oberpfalz-nord",
          "name": "Sparkasse Oberpfalz Nord",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-odenwaldkreis",
          "name": "Sparkasse Odenwaldkreis",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-oder-spree",
          "name": "Sparkasse Oder-Spree",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-offenburg-ortenau",
          "name": "Sparkasse Offenburg/Ortenau",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-olpe-drolshagen-wenden",
          "name": "Sparkasse Olpe-Drolshagen-Wenden",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-osnabrueck",
          "name": "Sparkasse Osnabrück",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-osterode-am-harz",
          "name": "Sparkasse Osterode am Harz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-paderborn-detmold",
          "name": "Sparkasse Paderborn-Detmold-Höxter",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-passau",
          "name": "Sparkasse Passau",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-pforzheim-calw",
          "name": "Sparkasse Pforzheim Calw",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-pfullendorf-messkirch",
          "name": "Sparkasse Pfullendorf-Meßkirch",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-prignitz",
          "name": "Sparkasse Prignitz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-rastatt-gernsbach",
          "name": "Sparkasse Rastatt-Gernsbach",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-regen-viechtach",
          "name": "Sparkasse Regen-Viechtach",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-rhein-neckar-nord",
          "name": "Sparkasse Rhein Neckar Nord",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-rhein-haardt",
          "name": "Sparkasse Rhein-Haardt",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-rhein-maas",
          "name": "Sparkasse Rhein-Maas",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-rhein-nahe",
          "name": "Sparkasse Rhein-Nahe",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-rosenheim-bad-aibling",
          "name": "Sparkasse Rosenheim-Bad Aibling",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-rotenburg-osterholz",
          "name": "Sparkasse Rotenburg Osterholz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-rottal-inn",
          "name": "Sparkasse Rottal-Inn",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-salem-heiligenberg",
          "name": "Sparkasse Salem-Heiligenberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-schaumburg",
          "name": "Sparkasse Schaumburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-scheessel",
          "name": "Sparkasse Scheeßel",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-schwarzwald-baar",
          "name": "Sparkasse Schwarzwald-Baar",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-schweinfurt-hassberge",
          "name": "Sparkasse Schweinfurt-Haßberge",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-schwaebisch-hall-crailsheim",
          "name": "Sparkasse Schwäbisch Hall-Crailsheim",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-siegen",
          "name": "Sparkasse Siegen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-sonneberg",
          "name": "Sparkasse Sonneberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-spree-neisse",
          "name": "Sparkasse Spree-Neiße",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-stblasien",
          "name": "Sparkasse St. Blasien",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-stade-altes-land",
          "name": "Sparkasse Stade - Altes Land",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-starkenburg",
          "name": "Sparkasse Starkenburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-staufen-breisach",
          "name": "Sparkasse Staufen-Breisach",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-suedholstein",
          "name": "Sparkasse Südholstein",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-sudpfalz",
          "name": "Sparkasse Südpfalz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-suedwestpfalz",
          "name": "Sparkasse Südwestpfalz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-tauberfranken",
          "name": "Sparkasse Tauberfranken",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-trier",
          "name": "Sparkasse Trier",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-uckermark",
          "name": "Sparkasse Uckermark",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-uecker-randow",
          "name": "Sparkasse Uecker-Randow",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-uelzen-luechow-dannenberg",
          "name": "Sparkasse Uelzen Lüchow-Dannenberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-ulm",
          "name": "Sparkasse Ulm",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-unnakamen",
          "name": "Sparkasse UnnaKamen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-unstrut-hainich",
          "name": "Sparkasse Unstrut-Hainich",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-vest-recklinghausen",
          "name": "Sparkasse Vest Recklinghausen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-vogtland",
          "name": "Sparkasse Vogtland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-vorderpfalz",
          "name": "Sparkasse Vorderpfalz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-vorpommern",
          "name": "Sparkasse Vorpommern",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-waldeck-frankenberg",
          "name": "Sparkasse Waldeck-Frankenberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-werra-meissner",
          "name": "Sparkasse Werra-Meißner",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-westerwald-sieg",
          "name": "Sparkasse Westerwald-Sieg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-westholstein",
          "name": "Sparkasse Westholstein",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-westmuensterland",
          "name": "Sparkasse Westmünsterland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-wetzlar",
          "name": "Sparkasse Wetzlar",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-wiesental",
          "name": "Sparkasse Wiesental",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-wilhelmshaven",
          "name": "Sparkasse Wilhelmshaven",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-witten",
          "name": "Sparkasse Witten",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-wittenberg",
          "name": "Sparkasse Wittenberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-wittgenstein",
          "name": "Sparkasse Wittgenstein",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-wolfach",
          "name": "Sparkasse Wolfach",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-worms-alzey-ried",
          "name": "Sparkasse Worms-Alzey-Ried",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-zollernalb",
          "name": "Sparkasse Zollernalb",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-zwickau",
          "name": "Sparkasse Zwickau",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-am-niederrhein",
          "name": "Sparkasse am Niederrhein",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-hagenherdecke",
          "name": "Sparkasse an Volme und Ruhr",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "sparkasse-an-der-lippe",
          "name": "Sparkasse an der Lippe",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-im-landkreis-cham",
          "name": "Sparkasse im Landkreis Cham",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-neustadt-ad-aisch-bad-windsheim",
          "name": "Sparkasse im Landkreis Neustadt a.d.Aisch-Bad Winsheim",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-im-landkreis-schwandorf",
          "name": "Sparkasse im Landkreis Schwandorf",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spk-zu-luebeck-ag",
          "name": "Sparkasse zu Lübeck",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "sparkassecelle-gifhorn-wolfsburg",
          "name": "Sparkasse Celle-Gifhorn-Wolfsburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "spreewaldbank",
          "name": "Spreewaldbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "onlinebanking-sgkb",
          "name": "St. Galler Kantonalbank Deutschland AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/25c7d723-6aba-4c1d-96d7-821a2f2cc5d3"
        },
        {
          "externalId": "spk-erlangen-hoechstadt-herzogenaurach",
          "name": "Stadt und Kreissparkasse Erlangen Höchstadt Herzogenaurach",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "stadt-und-kreissparkasse-leipzig",
          "name": "Stadt- und Kreissparkasse Leipzig",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-augsburg",
          "name": "Stadtsparkasse Augsburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-bad-pyrmont",
          "name": "Stadtsparkasse Bad Pyrmont",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-barsinghausen",
          "name": "Stadtsparkasse Barsinghausen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-bocholt",
          "name": "Stadtsparkasse Bocholt",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-burgdorf",
          "name": "Stadtsparkasse Burgdorf",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-cuxhaven",
          "name": "Stadtsparkasse Cuxhaven",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-dessau",
          "name": "Stadtsparkasse Dessau",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-duesseldorf",
          "name": "Stadtsparkasse Düsseldorf",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-grebenstein",
          "name": "Stadtsparkasse Grebenstein",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-haan-rheinlan",
          "name": "Stadtsparkasse Haan (Rheinland)",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-langenfeld-rheinlan",
          "name": "Stadtsparkasse Langenfeld (Rheinland)",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-lengerich",
          "name": "Stadtsparkasse Lengerich",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-magdeburg",
          "name": "Stadtsparkasse Magdeburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-moenchengladbach",
          "name": "Stadtsparkasse Mönchengladbach",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-offenbach",
          "name": "Stadtsparkasse Offenbach",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-rahden",
          "name": "Stadtsparkasse Rahden",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-remscheid",
          "name": "Stadtsparkasse Remscheid",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-rheine",
          "name": "Stadtsparkasse Rheine",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-schwedt",
          "name": "Stadtsparkasse Schwedt",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-schwelm",
          "name": "Stadtsparkasse Schwelm",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-solingen",
          "name": "Stadtsparkasse Solingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-wedel",
          "name": "Stadtsparkasse Wedel",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-wermelskirchen",
          "name": "Stadtsparkasse Wermelskirchen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-wunstorf",
          "name": "Stadtsparkasse Wunstorf",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "ssk-wuppertal",
          "name": "Stadtsparkasse Wuppertal",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "starling_ob",
          "name": "Starling",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/33318c83-aecd-45e7-aa33-882e5de1b092"
        },
        {
          "externalId": "steyler-bank",
          "name": "Steyler Bank GmbH",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/82b7f53e-d6eb-48f0-87cd-f7e82b9665b4"
        },
        {
          "externalId": "sylter-bank",
          "name": "Sylter Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "onlinebanking-suedspa",
          "name": "Südtiroler Sparkasse AG Niederlassung München",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "sudwestbank",
          "name": "Südwestbank AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/3e74200b-d1c8-46ef-874f-b915b26cb2a3"
        },
        {
          "externalId": "weg-bank",
          "name": "TEN31 Bank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "tsb",
          "name": "TSB Bank",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/626cd8d9-5b54-4b0b-b3d1-a621890e7446"
        },
        {
          "externalId": "taunus-sparkasse",
          "name": "Taunus Sparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "tescobank",
          "name": "Tesco Bank",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/b0152001-2af2-4f31-a713-0bda70339bfb"
        },
        {
          "externalId": "tide",
          "name": "Tide",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/c8d4547b-5c35-43c2-ae21-e902268c5886"
        },
        {
          "externalId": "tomorrow_de",
          "name": "Tomorrow Bank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/08325fb0-5435-43da-a56a-8fba0f7c8ec5"
        },
        {
          "externalId": "triodos-bank",
          "name": "Triodos Bank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/6ba0d6d8-b1b4-41a5-a3b9-6ab7f6f7511d"
        },
        {
          "externalId": "unicreditbusiness_de",
          "name": "UC eBanking Global",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/674624f1-e1f3-4c1a-9a38-f527a856482e"
        },
        {
          "externalId": "union-bank",
          "name": "UNION-BANK AG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/d3ac76ec-1f3f-4517-af03-3893561ea4ac"
        },
        {
          "externalId": "ulsterbank_uk",
          "name": "Ulster Bank (UK)",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/6772712b-149c-48ba-b0ce-905887fda292"
        },
        {
          "externalId": "ulsterbankbusiness_uk",
          "name": "Ulster Bank Bankline (NI)",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/6772712b-149c-48ba-b0ce-905887fda292"
        },
        {
          "externalId": "ulsterbank-clearspend_uk",
          "name": "Ulster Bank NI ClearSpend",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/8eaf3207-946a-4da9-9034-047e709f3fb6"
        },
        {
          "externalId": "unicajabanco-sandbox",
          "name": "Unicaja Banco Sandbox",
          "countries": ["ES"],
          "logo": "https://images.yapily.com/image/d0386e04-e84b-4a36-ad98-1d98126d2b09"
        },
        {
          "externalId": "unitytrust",
          "name": "Unity Trust",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/b3be6cc5-576f-4328-ba46-e7d6da930f08"
        },
        {
          "externalId": "vbu-volksbank",
          "name": "VBU Volksbank im Unterland eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-amberg",
          "name": "VR Bank Amberg-Sulzbach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-augsburg-ostallgaeu",
          "name": "VR Bank Augsburg-Ostallgäu eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbrb",
          "name": "VR Bank Bad Orb-Gelnhausen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-bamberg-forchheim",
          "name": "VR Bank Bamberg-Forchheim eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-bayreuth-hof",
          "name": "VR Bank Bayreuth-Hof eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vobadreieich",
          "name": "VR Bank Dreieich-Offenbach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbankfulda",
          "name": "VR Bank Fulda eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-sha",
          "name": "VR Bank Heilbronn Schwäbisch Hall eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-hessenland",
          "name": "VR Bank HessenLand eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrb-eisenach",
          "name": "VR Bank Ihre Heimatbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-ke-oa",
          "name": "VR Bank Kempten-Oberallgäu",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrkt",
          "name": "VR Bank Kitzingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-lahndill",
          "name": "VR Bank Lahn-Dill eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrblausitz",
          "name": "VR Bank Lausitz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-mkb",
          "name": "VR Bank Main-Kinzig-Büdingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbankmecklenburg",
          "name": "VR Bank Mecklenburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrmeinebank",
          "name": "VR Bank Metropolregion Nürnberg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-mittelhaardt",
          "name": "VR Bank Mittelhaardt eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-burglengenfeld",
          "name": "VR Bank Mittlere Oberpfalz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-mittlere",
          "name": "VR Bank Mittlere Oberpfalz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbankeg",
          "name": "VR Bank Monheim am Rhein",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-bank-muenchen-land",
          "name": "VR Bank München Land eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-neuburg-rain",
          "name": "VR Bank Neuburg-Rain eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "meine-bank-no",
          "name": "VR Bank Niederbayern-Oberpfalz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbanknord",
          "name": "VR Bank Nord eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-ofrm",
          "name": "VR Bank Oberfranken Mitte eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-onburgland",
          "name": "VR Bank Oldenburg Land eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank",
          "name": "VR Bank Rhein-Neckar eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "raiba-ried",
          "name": "VR Bank Ried-Überwald eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-sl-mh",
          "name": "VR Bank Schleswig-Mittelholstein eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-welzheim",
          "name": "VR Bank Schwäbischer Wald eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrsta",
          "name": "VR Bank Starnberg-Herrsching-Landsberg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-sww",
          "name": "VR Bank Südliche Weinstraße-Wasgau eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-suedpfalz",
          "name": "VR Bank Südpfalz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-weimar",
          "name": "VR Bank Weimar eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-westkueste",
          "name": "VR Bank Westküste eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbankgl",
          "name": "VR Bank eG Bergisch Gladbach-Leverkusen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-hw",
          "name": "VR Bank eG Heuberg-Winterlingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-alsheim",
          "name": "VR Bank eG, Alsheim",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-feuchtwangen-dinkelsbuehl",
          "name": "VR Bank im südlichen Franken",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-in-holstein",
          "name": "VR Bank in Holstein eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrb-westthueringen",
          "name": "VR Bank in Thüringen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "meine-vrbank",
          "name": "VR Bank zwischen den Meeren",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-genobank",
          "name": "VR GenoBank DonauWald eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-plus",
          "name": "VR PLUS Altmark-Wendland eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-partnerbank",
          "name": "VR PartnerBank eG Chattengau-Schwalm-Eder",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "bnseebank",
          "name": "VR Süd Bank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-ehingen-hochstraess",
          "name": "VR-Bank Alb-Blau-Donau eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-altenburgerland",
          "name": "VR-Bank Altenburger Land eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrb-meinebank",
          "name": "VR-Bank Bad Salzungen Schmalkalden eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-coburg",
          "name": "VR-Bank Coburg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-dm",
          "name": "VR-Bank Donau-Mindel eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrben",
          "name": "VR-Bank Ehningen-Nufringen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-ellwangen",
          "name": "VR-Bank Ellwangen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-bank-erding",
          "name": "VR-Bank Erding eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-ff",
          "name": "VR-Bank Fichtelgebirge- Frankenwald eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-internet",
          "name": "VR-Bank Fläming-Elsterland eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-bank-frenberg-nirfischbach",
          "name": "VR-Bank Freudenberg-Niederfischbach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-hg",
          "name": "VR-Bank Handels- und Gewerbebank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-isar-vils",
          "name": "VR-Bank Isar-Vils eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-ihn",
          "name": "VR-Bank Ismaning Hallbergmoos Neufahrn eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbanklm",
          "name": "VR-Bank Landau-Mengkofen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-ll",
          "name": "VR-Bank Landsberg-Ammersee eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrla",
          "name": "VR-Bank Landshut eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-lif-ebn",
          "name": "VR-Bank Lichtenfels-Ebern eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vorne",
          "name": "VR-Bank Ludwigsburg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-raiffeisenbank-rhoen-grabfeld",
          "name": "VR-Bank Main-Rhön eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-memmingen",
          "name": "VR-Bank Memmingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbankmitte",
          "name": "VR-Bank Mitte eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-mfr",
          "name": "VR-Bank Mittelfranken West eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-bank-mittelsachsen",
          "name": "VR-Bank Mittelsachsen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrnu",
          "name": "VR-Bank Neu-Ulm eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-bank-nordrhoen",
          "name": "VR-Bank NordRhön eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-banknoifel",
          "name": "VR-Bank Nordeifel eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-ostalb",
          "name": "VR-Bank Ostalb eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-obm",
          "name": "VR-Bank Ostbayern-Mitte eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-bank-passau",
          "name": "VR-Bank Passau eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbankrheinsieg",
          "name": "VR-Bank Rhein-Sieg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbk",
          "name": "VR-Bank Rottal-Inn eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrb-spangenberg",
          "name": "VR-Bank Spangenberg-Morschen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-suedwestpfalz",
          "name": "VR-Bank Südwestpfalz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-bank-online",
          "name": "VR-Bank Taufkirchen-Dorfen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrb-uckermark-randow",
          "name": "VR-Bank Uckermark-Randow eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-wenfels",
          "name": "VR-Bank Werdenfels eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-mw",
          "name": "VR-Bank eG Magstadt-Weissach",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-osnordland",
          "name": "VR-Bank eG Osnabrücker Nordland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-eg",
          "name": "VR-Bank eG, Würselen ",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-miba",
          "name": "VR-Bank in Mittelbaden eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbanksn",
          "name": "VR-Bank in Südniedersachsen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-suedonburg",
          "name": "VR-Bank in Südoldenburg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-bankverein",
          "name": "VR-Bankverein Bad Hersfeld-Rotenburg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-geo",
          "name": "VR-MainBank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vakifbank-intl",
          "name": "VakifBank International AG, Wien ZN Deutschland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/6ac17c70-465f-44a0-b6b4-98eb36897ee0"
        },
        {
          "externalId": "vanquis-bank",
          "name": "Vanquis Bank",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/eeb4929a-5c4d-4671-b884-1c6d78594ac9"
        },
        {
          "externalId": "verbundvolksbank-owl",
          "name": "VerbundVolksbank OWL eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "rb-burgstaedt",
          "name": "Vereinigte Raiffeisenbank Burgstädt eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vereinigte-sparkasse-im-maerkischen-kreis",
          "name": "Vereinigte Sparkasse im Märkischen Kreis",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "vvrbank-krp",
          "name": "Vereinigte VR Bank Kur- und Rheinpfalz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vereinigte-vrbk",
          "name": "Vereinigte VR Bank eG, Wyk auf Föhr",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-hunsrueck-nahe",
          "name": "Vereinigte Volksbank Raiffeisenbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vvrb",
          "name": "Vereinigte Volksbank Raiffeisenbank eG, Reinheim ",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vereinigte-ghbl",
          "name": "Vereinigte Volksbank eG Ganderkesee - Hude- Bookholzberg - Lemwerder",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "meine-vvb",
          "name": "Vereinigte Volksbank eG Saarlouis - Losheim am See - Sulzbach/Saar",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "v-vb",
          "name": "Vereinigte Volksbank eG, Brakel",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-osnabrueck",
          "name": "Vereinigte Volksbank eG Bramgau Osnabrück Wittlage",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "diebank",
          "name": "Vereinigte Volksbanken eG, Sindelfingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vereinte-volksbank",
          "name": "Vereinte Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vientin",
          "name": "VietinBank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/e856f63a-90b3-415c-8029-c6b562236c54"
        },
        {
          "externalId": "virginmoney",
          "name": "Virgin Money",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/fcac9f73-b37c-44a7-a46c-6047c5966c5b"
        },
        {
          "externalId": "virginmoney_essential",
          "name": "Virgin Money (Essential)",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/2bc464ec-2ef8-4ddb-bb21-7246c8ae988a"
        },
        {
          "externalId": "vrbfw",
          "name": "Volks- und Raiffeisenbank Fürstenwalde Seelow-Wriezen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-bank-muntal",
          "name": "Volks- und Raiffeisenbank Muldental eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbprignitz",
          "name": "Volks- und Raiffeisenbank Prignitz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbsaale-unstrut",
          "name": "Volks- und Raiffeisenbank Saale-Unstrut eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-vilshofen",
          "name": "Volksbank - Raiffeisenbank Vilshofen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-laichinger-alb",
          "name": "Volksbank Alb eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-albstadt",
          "name": "Volksbank Albstadt eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-allgaeu-oberschwaben",
          "name": "Volksbank Allgäu-Oberschwaben eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-alzey-worms",
          "name": "Volksbank Alzey-Worms eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-anroechte",
          "name": "Volksbank Anröchte eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-ascheberg-herbern",
          "name": "Volksbank Ascheberg-Herbern eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-brawo",
          "name": "Volksbank BRAWO",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-backnang",
          "name": "Volksbank Backnang eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbbs",
          "name": "Volksbank Bad Salzuflen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-bad-saulgau",
          "name": "Volksbank Bad Saulgau eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-baumberge",
          "name": "Volksbank Baumberge eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-bl",
          "name": "Volksbank Beckum-Lippstadt eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-bia",
          "name": "Volksbank Beilstein-Ilsfeld- Abstatt eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-berg",
          "name": "Volksbank Berg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-bi-gt",
          "name": "Volksbank Bielefeld-Gütersloh eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-bocholt",
          "name": "Volksbank Bocholt eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-bochumwitten",
          "name": "Volksbank Bochum Witten eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-brandoberndorf",
          "name": "Volksbank Brandoberndorf eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbbraunlage",
          "name": "Volksbank Braunlage eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-breisgau-nord",
          "name": "Volksbank Breisgau Nord eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-breisgau-markgraeflerland",
          "name": "Volksbank Breisgau- Markgräflerland eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbbremennord",
          "name": "Volksbank Bremen-Nord eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-brenztal",
          "name": "Volksbank Brenztal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-butzbach",
          "name": "Volksbank Butzbach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-boenen",
          "name": "Volksbank Bönen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-boe-bernburg",
          "name": "Volksbank Börde-Bernburg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbbh",
          "name": "Volksbank Börßum-Hornburg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-buehl",
          "name": "Volksbank Bühl eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-chemnitz",
          "name": "Volksbank Chemnitz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-dan",
          "name": "Volksbank Daaden eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vobda",
          "name": "Volksbank Dammer Berge eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vobaisslingen",
          "name": "Volksbank Deisslingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-rietberg",
          "name": "Volksbank Delbrück-Rietberg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vblitzsch",
          "name": "Volksbank Delitzsch eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbankmmin",
          "name": "Volksbank Demmin eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vssau",
          "name": "Volksbank Dessau-Anhalt eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbankttenhausen",
          "name": "Volksbank Dettenhausen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbdonw",
          "name": "Volksbank Dortmund-Nordwest eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb3",
          "name": "Volksbank Dreiländereck eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-dren-bautzen",
          "name": "Volksbank Dresden-Bautzen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vobadhk",
          "name": "Volksbank Dünnwald-Holweide eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "deine-volksbank",
          "name": "Volksbank Düsseldorf Neuss eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbankeisenberg",
          "name": "Volksbank Eisenberg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-elsen-wewer-borchen",
          "name": "Volksbank Elsen-Wewer-Borchen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "dervolksbanker",
          "name": "Volksbank Emmerich-Rees eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-emstal",
          "name": "Volksbank Emstal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-eow",
          "name": "Volksbank Enniger-Ostenfelde-Westkirchen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-erft",
          "name": "Volksbank Erft eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-ermstal-alb",
          "name": "Volksbank Ermstal-Alb eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-ettlingen",
          "name": "Volksbank Ettlingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "eu-banking",
          "name": "Volksbank Euskirchen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbeutin",
          "name": "Volksbank Eutin Raiffeisenbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-feldatal",
          "name": "Volksbank Feldatal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-fir",
          "name": "Volksbank Filder eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-flein-talheim",
          "name": "Volksbank Flein-Talheim eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-franken",
          "name": "Volksbank Franken eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-freiburg",
          "name": "Volksbank Freiburg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-fntt",
          "name": "Volksbank Friedrichshafen-Tettnang eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbghb",
          "name": "Volksbank GMHütte-Hagen-Bissendorf eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbgebhardshain",
          "name": "Volksbank Gebhardshain eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbgeest",
          "name": "Volksbank Geest eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbgn",
          "name": "Volksbank Geeste-Nord eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-gescher",
          "name": "Volksbank Gescher eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-glm",
          "name": "Volksbank Glan-Münchweiler eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbga",
          "name": "Volksbank Gronau-Ahaus eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-goeppingen",
          "name": "Volksbank Göppingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbhalle",
          "name": "Volksbank Halle (Saale) eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-halle",
          "name": "Volksbank Halle/Westf. eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-hameln-stadthagen",
          "name": "Volksbank Hameln-Stadthagen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-hamm",
          "name": "Volksbank Hamm / Sieg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbhaseluenne",
          "name": "Volksbank Haselünne eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbhen",
          "name": "Volksbank Heiden eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-heimbach",
          "name": "Volksbank Heimbach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-heinsberg",
          "name": "Volksbank Heinsberg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-hellweg",
          "name": "Volksbank Hellweg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-heuchelheim",
          "name": "Volksbank Heuchelheim eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-hochrhein",
          "name": "Volksbank Hochrhein eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-hohenlimburg",
          "name": "Volksbank Hohenlimburg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-hohenlohe",
          "name": "Volksbank Hohenlohe eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-hoba",
          "name": "Volksbank Hohenzollern-Balingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-immenstadt",
          "name": "Volksbank Immenstadt eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-jerichower-land",
          "name": "Volksbank Jerichower Land eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-jever",
          "name": "Volksbank Jever eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vobakl",
          "name": "Volksbank Kaiserslautern eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-kassel-goettingen",
          "name": "Volksbank Kassel Göttingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-kempen",
          "name": "Volksbank Kempen-Grefrath eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-kierspe",
          "name": "Volksbank Kierspe eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-kirnau",
          "name": "Volksbank Kirnau-Krautheim eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-kw",
          "name": "Volksbank Klettgau-Wutöschingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-kleverland",
          "name": "Volksbank Kleverland eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbkraichgau",
          "name": "Volksbank Kraichgau eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbkrefeld",
          "name": "Volksbank Krefeld eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-kurpfalz",
          "name": "Volksbank Kurpfalz eG ",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-koeln-bonn",
          "name": "Volksbank Köln Bonn eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-lahr",
          "name": "Volksbank Lahr eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-langernbach",
          "name": "Volksbank Langendernbach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vblastrup",
          "name": "Volksbank Lastrup eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-aktiv",
          "name": "Volksbank Lauterbach-Schlitz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-lauterecken",
          "name": "Volksbank Lauterecken eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbleos",
          "name": "Volksbank Leonberg-Strohgäu eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-limbach",
          "name": "Volksbank Limbach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-linberg",
          "name": "Volksbank Lindenberg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vblohne-muehlen",
          "name": "Volksbank Lohne-Dinklage-Steinfeld-Mühlen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-loebau-zittau",
          "name": "Volksbank Löbau-Zittau eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-luebeck",
          "name": "Volksbank Lübeck eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vblh",
          "name": "Volksbank Lüneburger Heide eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-maburg",
          "name": "Volksbank Magdeburg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vobamt",
          "name": "Volksbank Main-Tauber eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-mainspitze",
          "name": "Volksbank Mainspitze eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-marl-recklinghausen",
          "name": "Volksbank Marl-Recklinghausen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-mittelhessen",
          "name": "Volksbank Mittelhessen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "v-mn",
          "name": "Volksbank Mittlerer Neckar eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-msw",
          "name": "Volksbank Mittlerer Schwarzwald eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-mittleres-erzgebirge",
          "name": "Volksbank Mittleres Erzgebirge eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-mittweida",
          "name": "Volksbank Mittweida eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-moeckmuehl",
          "name": "Volksbank Möckmühl eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-mg",
          "name": "Volksbank Mönchengladbach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-muensingen",
          "name": "Volksbank Münsingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-mn",
          "name": "Volksbank Münsterland Nord eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-neckartal",
          "name": "Volksbank Neckartal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-nirgrafschaft",
          "name": "Volksbank Niedergrafschaft eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-nirrhein",
          "name": "Volksbank Niederrhein eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbanksulingen",
          "name": "Volksbank Niedersachsen-Mitte",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbnh",
          "name": "Volksbank Nordharz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-nordhuemmling",
          "name": "Volksbank Nordhümmling eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-nordschwarzwald",
          "name": "Volksbank Nordschwarzwald eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-nottuln",
          "name": "Volksbank Nottuln eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-ober-moerlen",
          "name": "Volksbank Ober-Mörlen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-oberberg",
          "name": "Volksbank Oberberg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbol",
          "name": "Volksbank Ochtrup-Laer eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-owd",
          "name": "Volksbank Olpe-Wenden-Drolshagen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-ostlippe",
          "name": "Volksbank Ostlippe eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vboyten",
          "name": "Volksbank Oyten eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-luebbecker-land",
          "name": "Volksbank PLUS",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-pfullendorf",
          "name": "Volksbank Pfullendorf eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-pirna",
          "name": "Volksbank Pirna eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-plochingen",
          "name": "Volksbank Plochingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-raesfeld",
          "name": "Volksbank Raesfeld und Erle eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-ammerbuch",
          "name": "Volksbank Raiffeisenbank AmmerGäu",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "die-vrbank",
          "name": "Volksbank Raiffeisenbank Bad Kissingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-bayernmitte",
          "name": "Volksbank Raiffeisenbank Bayern Mitte eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-dachau",
          "name": "Volksbank Raiffeisenbank Dachau eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-ffb",
          "name": "Volksbank Raiffeisenbank Fürstenfeldbruck eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-meissen",
          "name": "Volksbank Raiffeisenbank Meißen Großenhain eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrb-nirschlesien",
          "name": "Volksbank Raiffeisenbank Niederschlesien eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-nordoberpfalz",
          "name": "Volksbank Raiffeisenbank Nordoberpfalz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-obb-so",
          "name": "Volksbank Raiffeisenbank Oberbayern Südost eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-raiffeisenbank-regensburg-schwandorf",
          "name": "Volksbank Raiffeisenbank Regensburg-Schwandorf eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-bank-wuerzburg",
          "name": "Volksbank Raiffeisenbank Würzburg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrhs",
          "name": "Volksbank Raiffeisenbank eG, Itzehoe",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbrn",
          "name": "Volksbank Rathenow eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-remseck",
          "name": "Volksbank Remseck eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-rh",
          "name": "Volksbank Rhede eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-rhein-erft-koeln",
          "name": "Volksbank Rhein-Erft-Köln eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-rll",
          "name": "Volksbank Rhein-Lahn-Limburg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-rhein-lippe",
          "name": "Volksbank Rhein-Lippe eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-rnh",
          "name": "Volksbank Rhein-Nahe-Hunsrück eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-rhein-ruhr",
          "name": "Volksbank Rhein-Ruhr eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-rheinahreifel",
          "name": "Volksbank RheinAhrEifel eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-rheinboellen",
          "name": "Volksbank Rheinböllen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-riesa",
          "name": "Volksbank Riesa eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-rot",
          "name": "Volksbank Rot eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-rottweil",
          "name": "Volksbank Rottweil eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-ruhrmitte",
          "name": "Volksbank Ruhr Mitte eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vobasandhofen",
          "name": "Volksbank Sandhofen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-bigge-lenne",
          "name": "Volksbank Sauerland eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-schermbeck",
          "name": "Volksbank Schermbeck eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-schlangen",
          "name": "Volksbank Schlangen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-schupbach",
          "name": "Volksbank Schupbach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-schwanew",
          "name": "Volksbank Schwanewede eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbsdn",
          "name": "Volksbank Schwarzwald-Donau-Neckar eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-seligenstadt",
          "name": "Volksbank Seligenstadt eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-selm-bork",
          "name": "Volksbank Selm-Bork eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-sen",
          "name": "Volksbank Senden eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbsolling",
          "name": "Volksbank Solling eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbspn",
          "name": "Volksbank Spree-Neiße eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbsprockhoevel",
          "name": "Volksbank Sprockhövel eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vobaeg",
          "name": "Volksbank Stade-Cuxhaven eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-staufen",
          "name": "Volksbank Staufen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbstendal",
          "name": "Volksbank Stendal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-stuttgart",
          "name": "Volksbank Stuttgart eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-sh",
          "name": "Volksbank Störmede-Hörste eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-sulmtal",
          "name": "Volksbank Sulmtal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbsumsland",
          "name": "Volksbank Süd-Emsland eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-nordkirchen",
          "name": "Volksbank Südkirchen-Capelle-Nordkirchen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vrbank-suedthueringen",
          "name": "Volksbank Thüringen Mitte eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-eifel",
          "name": "Volksbank Trier Eifel eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-trossingen",
          "name": "Volksbank Trossingen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-ue-saw",
          "name": "Volksbank Uelzen-Salzwedel eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-ulm-biberach",
          "name": "Volksbank Ulm-Biberach eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-ulrichstein",
          "name": "Volksbank Ulrichstein eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbvechta",
          "name": "Volksbank Vechta eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-versmold",
          "name": "Volksbank Versmold eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbankviersen",
          "name": "Volksbank Viersen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbvisbek",
          "name": "Volksbank Visbek eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-vso",
          "name": "Volksbank Vogtland-Saale-Orla eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "pommerschevb",
          "name": "Volksbank Vorpommern",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-weschnitztal",
          "name": "Volksbank Weschnitztal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-westenholz",
          "name": "Volksbank Westenholz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-westerst",
          "name": "Volksbank Westerstede eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-bank-westmuensterland",
          "name": "Volksbank Westmünsterland",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vobawhv",
          "name": "Volksbank Wilhelmshaven eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbwinsenermarsch",
          "name": "Volksbank Winsener Marsch eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-wittenberg",
          "name": "Volksbank Wittenberg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-wittgenstein",
          "name": "Volksbank Wittgenstein eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-wissmar",
          "name": "Volksbank Wißmar eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-worpsw",
          "name": "Volksbank Worpswede eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-zuff",
          "name": "Volksbank Zuffenhausen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-zwickau",
          "name": "Volksbank Zwickau eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-aw",
          "name": "Volksbank am Württemberg eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-niers",
          "name": "Volksbank an der Niers eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "gestalterbank",
          "name": "Volksbank eG - Die Gestalterbank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-eg",
          "name": "Volksbank eG Bad Laer-Borgloh-Hilter-Melle",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbessen-cappeln",
          "name": "Volksbank eG Emstek Essen Cappeln",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-vor-ort",
          "name": "Volksbank eG Gera · Jena · Rudolstadt",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vobakn",
          "name": "Volksbank eG Konstanz",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbohz",
          "name": "Volksbank eG Osterholz-Scharmbeck",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbsila",
          "name": "Volksbank eG Südheide - Isenhagener Land - Altmark",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-westrharfehn",
          "name": "Volksbank eG Westrhauderfehn",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "voba-fds",
          "name": "Volksbank eG im Kreis Freudenstadt",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-lebsen",
          "name": "Volksbank eG, Adelebsen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbfoa",
          "name": "Volksbank eG, Fredenbeck ",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "spadaka-friesoythe",
          "name": "Volksbank eG, Friesoythe",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-galegen",
          "name": "Volksbank eG, Gardelegen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-grebenhain",
          "name": "Volksbank eG, Grebenhain",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-eg",
          "name": "Volksbank eG, Hildesheim-Lehrte-Pattensen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-abi",
          "name": "Volksbank eG, Köthen-Bitterfeld",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbloeningen",
          "name": "Volksbank eG, Löningen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-sangerhausen",
          "name": "Volksbank eG, Sangerhausen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "myvoba",
          "name": "Volksbank eG, Seesen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-mit-herz",
          "name": "Volksbank eG, Wolfenbüttel",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-ueberlingen",
          "name": "Volksbank eG, Überlingen",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "bergische-volksbank",
          "name": "Volksbank im Bergischen Land eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbankeg",
          "name": "Volksbank im Elbe-Weser-Dreieck",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbimharz",
          "name": "Volksbank im Harz eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-im-hochsauerland",
          "name": "Volksbank im Hochsauerland eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-iw",
          "name": "Volksbank im Wesertal eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-is",
          "name": "Volksbank in Schaumburg und Nienburg",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbinswf",
          "name": "Volksbank in Südwestfalen eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-hm",
          "name": "Volksbank in der Hohen Mark eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vbhnr",
          "name": "Volksbank in der Region eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "volksbank-karlsruhe",
          "name": "Volksbank pur eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vb-ueberherrn",
          "name": "Volksbank Überherrn eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "vr-glauchau",
          "name": "Volksbank-Raiffeisenbank Glauchau eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "wacker-bank",
          "name": "Waldeck-Frankenberger Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "wartburg-sparkasse",
          "name": "Wartburg-Sparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "weberbank",
          "name": "Weberbank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/fc4b97ab-b8e5-4861-97d0-85a9f5a31ac5"
        },
        {
          "externalId": "weser-elbe-sparkasse",
          "name": "Weser-Elbe Sparkasse",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/178cf9d6-9f1d-4053-8b5c-4237ef3d5b8a"
        },
        {
          "externalId": "westerwaldbank",
          "name": "Westerwald Bank eG Volks- und Raiffeisenbank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "wvb",
          "name": "Wiesbadener Volksbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "winterbacher-bank",
          "name": "Winterbacher Bank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "wise-live",
          "name": "Wise",
          "countries": ["HU", "DE", "IE", "NL", "RO", "CH", "ES", "PT", "GB", "IT"],
          "logo": "https://images.yapily.com/image/78e570a4-3fcc-458d-b449-af9d1b09d462"
        },
        {
          "externalId": "woolsocks_de",
          "name": "Woolsocks Germany",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/e727c9a0-2645-487b-b0d1-660dd5ba2f6b"
        },
        {
          "externalId": "yorkshirebuildingsociety",
          "name": "Yorkshire Building Society",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/eeb082df-042f-4ec8-bbc4-7e6cd9f9d124"
        },
        {
          "externalId": "cashplus",
          "name": "Zempler Bank (Formerly Cashplus)",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/883cc7a4-f08b-4d94-aafc-73990d28896f"
        },
        {
          "externalId": "levo-bank",
          "name": "levoBank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/5315fd86-c1e5-4660-a2a6-f4ca2d0cb77b"
        },
        {
          "externalId": "vb-rb",
          "name": "meine Volksbank Raiffeisenbank eG",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/29034ca5-c465-4dc7-9aab-688bda8a69e5"
        },
        {
          "externalId": "deutschebank-noris_de",
          "name": "norisbank",
          "countries": ["DE"],
          "logo": "https://images.yapily.com/image/25fb3af7-0046-470e-adac-862129edb253"
        },
        {
          "externalId": "thinkmoney",
          "name": "thinkmoney",
          "countries": ["GB"],
          "logo": "https://images.yapily.com/image/0d41e6b6-c0cc-49e0-98a3-5ea10acddf42"
        }
      ]
  })
  }),

  // Catch-all for any unhandled API GET requests
  http.get('/api/*', ({ request }) => {
    console.log('[Demo MSW] Unhandled GET:', request.url);
    return HttpResponse.json({});
  }),

  // Catch-all for any unhandled API POST requests
  http.post('/api/*', async ({ request }) => {
    console.log('[Demo MSW] Unhandled POST:', request.url);
    const body = (await request.json().catch(() => ({}))) as Record<string, unknown>;
    return HttpResponse.json({
      success: true,
      ...body,
    });
  }),

  // Catch-all for any unhandled API PATCH requests
  http.patch('/api/*', async ({ request }) => {
    console.log('[Demo MSW] Unhandled PATCH:', request.url);
    const body = (await request.json().catch(() => ({}))) as Record<string, unknown>;
    return HttpResponse.json({
      success: true,
      ...body,
    });
  }),

  // Catch-all for any unhandled API PUT requests
  http.put('/api/*', async ({ request }) => {
    console.log('[Demo MSW] Unhandled PUT:', request.url);
    const body = (await request.json().catch(() => ({}))) as Record<string, unknown>;
    return HttpResponse.json({
      success: true,
      ...body,
    });
  }),

  // Catch-all for any unhandled API DELETE requests
  http.delete('/api/*', ({ request }) => {
    console.log('[Demo MSW] Unhandled DELETE:', request.url);
    return HttpResponse.json({
      success: true,
      message: 'Deleted',
    });
  }),
];

function generateStaticApiEndpointMap() {
  const pathMethodMap = new Map<string, string[]>();

  for (const handler of staticHandlers) {
    console.log('handler', handler.info.path, typeof handler.info.path, handler.info.method, typeof handler.info.method);
    const path = handler.info.path;
    const method = handler.info.method;

    if (typeof path !== "string") {
      continue;
    }
    if (!pathMethodMap.has(path)) {
      pathMethodMap.set(path, []);
    }

    if (typeof method === "string") {
      pathMethodMap.get(path)!.push(method);
    }
    
  }
  return pathMethodMap;
}

export const staticApiEndpointMap = generateStaticApiEndpointMap();
export default staticHandlers;
